(function () {
  /* ==========================================================
     SOLUM CLAVIS — AI Concierge Chat  v5
     Features:
       - Fullscreen mobile (immersive, native-app feel)
       - iOS zoom prevention (viewport lock + font-size 16px)
       - Virtual-keyboard manager (visualViewport API)
       - Horizontal carousel for product cards on mobile
       - Header hide-on-scroll in fullscreen
       - Progress bar (thin gold, step X / Y, multilingual)
       - Visual tap micro-feedback (iOS + Android)
       - Premium confirmation card after submission (fully i18n)
       - Full i18n on all action buttons and UI labels
       - localStorage with 24 h expiry (replaces sessionStorage)
       - Smooth slide-in transitions on messages & actions
     ========================================================== */

  var API_BASE = "https://solum-concierge-api.onrender.com";
  var STORAGE_KEY = "sc_concierge_session";
  var STORAGE_TTL = 24 * 60 * 60 * 1000; // 24h
  var MOBILE_BP = 768;

  /* ========== helpers ========== */
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    if (attrs) Object.keys(attrs).forEach(function (k) {
      if (k === "class") node.className = attrs[k];
      else if (k === "html") node.innerHTML = attrs[k];
      else if (k === "text") node.textContent = attrs[k];
      else node.setAttribute(k, attrs[k]);
    });
    if (children) children.forEach(function (c) { node.appendChild(c); });
    return node;
  }
  function escapeHtml(s) {
    return String(s).replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;")
                     .replace(/"/g,"&quot;").replace(/'/g,"&#039;");
  }
  function linkifyText(text) {
    var e = escapeHtml(text || "");
    return e.replace(/\n/g,"<br>")
            .replace(/(https?:\/\/[^\s<]+)/g,'<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>');
  }
  function isMobile() { return window.innerWidth <= MOBILE_BP; }

  /* ========== localStorage with 24 h TTL ========== */
  function createFreshSessionId() {
    return "sc-" + Math.random().toString(36).slice(2) + "-" + Date.now();
  }
  function getSessionId() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        var obj = JSON.parse(raw);
        if (obj && obj.id && (Date.now() - obj.ts < STORAGE_TTL)) return obj.id;
      }
    } catch (_) {}
    return resetSessionId();
  }
  function resetSessionId() {
    var id = createFreshSessionId();
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ id: id, ts: Date.now() })); }
    catch (_) {}
    return id;
  }

  /* ========== locale ========== */
  function normalizeLocale(code) {
    var c = String(code || "").toLowerCase();
    if (c.startsWith("ko")) return "ko";
    if (c.startsWith("ja")) return "ja";
    if (c.includes("hk") || c.startsWith("zh")) return "zh-hk";
    if (c.startsWith("th")) return "th";
    return "en";
  }
  function defaultGreeting(locale) {
    if (locale === "ko") return "Solum Clavis에 오신 것을 환영합니다 — 유럽 럭셔리 컨시어지 서비스입니다.\n\n성함을 알려주실 수 있을까요?";
    if (locale === "ja") return "Solum Clavisへようこそ — ヨーロッパのラグジュアリーコンシェルジュサービスです。\n\nお名前を教えていただけますか。";
    if (locale === "zh-hk") return "歡迎來到 Solum Clavis — 你的歐洲奢侈品禮賓服務。\n\n可以先告訴我你的名字嗎？";
    if (locale === "th") return "ยินดีต้อนรับสู่ Solum Clavis — บริการคอนเซียร์จลักชัวรียุโรปของคุณ\n\nคุณชื่ออะไรคะ?";
    return "Welcome to Solum Clavis — your European luxury concierge.\n\nWhat's your name?";
  }

  /* ========== i18n dictionary ========== */
  var I18N = {
    en: {
      hero_tagline:    "Personal Sourcing Service",
      select:          "Select",
      confirm_send:    "Confirm & send",
      add_another:     "Add another product",
      finish:          "Review & finish",
      none_of_these:   "None of these",
      yes:             "Yes, correct",
      no_change:       "No, change it",
      skip:            "Skip",
      edit_last:       "Edit last product",
      remove_last:     "Remove last product",
      view_ref:        "View official reference",
      step_label:      function (s, t) { return "Step " + s + " / " + t; },
      summary_eyebrow: "Request summary",
      product_prefix:  "Product ",
      confirm_ref:     "REF",
      confirm_msg:     "Your request has been received. Our team will get back to you shortly.",
      confirm_submitted: "Request submitted",
      ph_ask_first_name:  "Your name…",
      ph_ask_email:       "Your email address…",
      ph_confirm_email:   "Tap a button above",
      ph_ask_market:      "Tap a button above",
      ph_ask_brand:       "Brand name…",
      ph_ask_product_input: "Describe the product…",
      ph_present_suggestions: "Tap a card above",
      ph_ask_color:       "Color preference…",
      ph_ask_size:        "Size (e.g. 42, M, OS)…",
      ph_ask_notes:       "Any special notes…",
      ph_recap:           "Tap a button above",
      ph_welcome:         "Your name…",
      ph_welcome_menu:    "Type your message or tap a button…",
      ph_qa_chat:         "Ask a question…",
      ph_ask_pro_code:    "Tap a button above",
      ph_enter_pro_code:  "Your access code…",
      ph_pro_reg_name:    "Your full name…",
      ph_pro_reg_company: "Company name…",
      ph_pro_reg_email:   "Professional email…",
      ph_pro_reg_market:  "Tap a button above",
      ph_pro_reg_address: "Your business address…",
      ph_pro_reg_phone:   "Phone number…",
      ph_pro_reg_website: "Your website URL…",
      ph_pro_reg_done:    "Tap a button above",
      ph_ps_entry:        "Tap a button above",
      ph_ps_enter_code:   "Your access code…",
      ph_ps_browse:       "Tap a product above",
      private_sale:       "Private Sale",
      ps_have_code:       "I have a code",
      ps_no_code:         "How to get a code?",
      ph_pro_order_menu:  "Tap a button above",
      ph_pro_check_brand: "Brand name…",
      ph_pro_check_price_input: "Price in EUR (e.g. 1200)…",
      ph_pro_check_result: "Tap a button above",
      ph_pro_order_destination: "Tap a button above",
      ph_pro_order_brand: "Brand name or product URL…",
      ph_pro_order_confirm_product: "Tap a button above",
      ph_pro_order_brand_manual: "Brand name…",
      ph_pro_order_product_desc: "Product description or URL…",
      ph_pro_order_size: "Tap a size above",
      ph_pro_order_size_manual: "Type your size…",
      ph_pro_order_color: "Tap a color above",
      ph_pro_order_color_manual: "Type your color…",
      ph_pro_order_quantity: "Quantity (e.g. 1)…",
      ph_pro_order_product_notes: "Special details for this product…",
      ph_pro_order_add_more: "Tap a button above",
      ph_pro_order_client_name: "Client's full name…",
      ph_pro_order_client_address: "Full address on one line…",
      ph_pro_order_client_postal_code: "Postal code…",
      ph_pro_order_client_phone: "Client's phone number…",
      ph_pro_order_client_customs_id: "P-XXXXXXXXXXXX",
      ph_pro_order_client_notes: "Additional info (optional)…",
      ph_pro_order_review: "Tap a button above",
      ph_default:         "Type your message…",
    },
    ko: {
      hero_tagline:    "퍼스널 소싱 서비스",
      select:          "선택",
      confirm_send:    "확인 후 전송",
      add_another:     "다른 제품 추가",
      finish:          "검토 및 완료",
      none_of_these:   "해당 없음",
      yes:             "네, 맞습니다",
      no_change:       "아니요, 변경",
      skip:            "건너뛰기",
      edit_last:       "마지막 제품 수정",
      remove_last:     "마지막 제품 삭제",
      view_ref:        "공식 레퍼런스 보기",
      step_label:      function (s, t) { return "단계 " + s + " / " + t; },
      summary_eyebrow: "요청 요약",
      product_prefix:  "제품 ",
      confirm_ref:     "REF",
      confirm_msg:     "요청이 접수되었습니다. 담당자가 곧 연락드리겠습니다.",
      confirm_submitted: "요청이 전송되었습니다",
      ph_ask_first_name:  "성함을 입력하세요…",
      ph_ask_email:       "이메일 주소…",
      ph_confirm_email:   "위 버튼을 선택하세요",
      ph_ask_market:      "위 버튼을 선택하세요",
      ph_ask_brand:       "브랜드 이름…",
      ph_ask_product_input: "제품 설명…",
      ph_present_suggestions: "위 카드를 선택하세요",
      ph_ask_color:       "색상 선호…",
      ph_ask_size:        "사이즈 (예: 42, M)…",
      ph_ask_notes:       "추가 메모…",
      ph_recap:           "위 버튼을 선택하세요",
      ph_welcome:         "성함…",
      ph_welcome_menu:    "메시지를 입력하거나 버튼을 선택하세요…",
      ph_qa_chat:         "질문을 입력하세요…",
      ph_ask_pro_code:    "위 버튼을 선택하세요",
      ph_enter_pro_code:  "접속 코드…",
      ph_pro_reg_name:    "전체 이름…",
      ph_pro_reg_company: "회사명…",
      ph_pro_reg_email:   "업무용 이메일…",
      ph_pro_reg_market:  "위 버튼을 선택하세요",
      ph_pro_reg_address: "사업장 주소…",
      ph_pro_reg_phone:   "전화번호…",
      ph_pro_reg_website: "웹사이트 URL…",
      ph_pro_reg_done:    "위 버튼을 선택하세요",
      ph_ps_entry:        "위 버튼을 선택하세요",
      ph_ps_enter_code:   "액세스 코드…",
      ph_ps_browse:       "위 상품을 선택하세요",
      private_sale:       "프라이빗 세일",
      ps_have_code:       "코드가 있어요",
      ps_no_code:         "코드 받는 방법",
      ph_pro_order_menu:  "위 버튼을 선택하세요",
      ph_pro_check_brand: "브랜드명…",
      ph_pro_check_price_input: "EUR 가격 (예: 1200)…",
      ph_pro_check_result: "위 버튼을 선택하세요",
      ph_pro_order_destination: "위 버튼을 선택하세요",
      ph_pro_order_brand: "브랜드명 또는 제품 URL…",
      ph_pro_order_confirm_product: "위 버튼을 선택하세요",
      ph_pro_order_brand_manual: "브랜드명…",
      ph_pro_order_product_desc: "제품 설명 또는 URL…",
      ph_pro_order_size: "위에서 사이즈를 선택하세요",
      ph_pro_order_size_manual: "사이즈를 입력하세요…",
      ph_pro_order_color: "위에서 색상을 선택하세요",
      ph_pro_order_color_manual: "색상을 입력하세요…",
      ph_pro_order_quantity: "수량 (예: 1)…",
      ph_pro_order_product_notes: "이 제품에 대한 세부 사항…",
      ph_pro_order_add_more: "위 버튼을 선택하세요",
      ph_pro_order_client_name: "고객 이름(성명)…",
      ph_pro_order_client_address: "한 줄로 전체 주소…",
      ph_pro_order_client_postal_code: "우편번호…",
      ph_pro_order_client_phone: "고객 전화번호…",
      ph_pro_order_client_customs_id: "P-XXXXXXXXXXXX",
      ph_pro_order_client_notes: "추가 정보 (선택)…",
      ph_pro_order_review: "위 버튼을 선택하세요",
      ph_default:         "메시지를 입력하세요…",
    },
    ja: {
      hero_tagline:    "パーソナル ソーシング サービス",
      select:          "選択",
      confirm_send:    "確認して送信",
      add_another:     "別の商品を追加",
      finish:          "確認して完了",
      none_of_these:   "該当なし",
      yes:             "はい、正しいです",
      no_change:       "いいえ、変更します",
      skip:            "スキップ",
      edit_last:       "最後の商品を編集",
      remove_last:     "最後の商品を削除",
      view_ref:        "公式リファレンスを見る",
      step_label:      function (s, t) { return "ステップ " + s + " / " + t; },
      summary_eyebrow: "依頼内容の概要",
      product_prefix:  "商品 ",
      confirm_ref:     "REF",
      confirm_msg:     "ご依頼を承りました。担当者より改めてご連絡いたします。",
      confirm_submitted: "依頼が送信されました",
      ph_ask_first_name:  "お名前…",
      ph_ask_email:       "メールアドレス…",
      ph_confirm_email:   "上のボタンを選択",
      ph_ask_market:      "上のボタンを選択",
      ph_ask_brand:       "ブランド名…",
      ph_ask_product_input: "商品の説明…",
      ph_present_suggestions: "上のカードを選択",
      ph_ask_color:       "色のご希望…",
      ph_ask_size:        "サイズ（例：42、M）…",
      ph_ask_notes:       "補足メモ…",
      ph_recap:           "上のボタンを選択",
      ph_welcome:         "お名前…",
      ph_welcome_menu:    "メッセージを入力またはボタンを選択…",
      ph_qa_chat:         "ご質問をどうぞ…",
      ph_ask_pro_code:    "上のボタンを選択",
      ph_enter_pro_code:  "アクセスコード…",
      ph_pro_reg_name:    "フルネーム…",
      ph_pro_reg_company: "会社名…",
      ph_pro_reg_email:   "お仕事用メール…",
      ph_pro_reg_market:  "上のボタンを選択",
      ph_pro_reg_address: "ビジネスの住所…",
      ph_pro_reg_phone:   "電話番号…",
      ph_pro_reg_website: "ウェブサイトURL…",
      ph_pro_reg_done:    "上のボタンを選択",
      ph_ps_entry:        "上のボタンを選択",
      ph_ps_enter_code:   "アクセスコード…",
      ph_ps_browse:       "上の商品を選択",
      private_sale:       "プライベートセール",
      ps_have_code:       "コードを持っています",
      ps_no_code:         "コードの取得方法",
      ph_pro_order_menu:  "上のボタンを選択",
      ph_pro_check_brand: "ブランド名…",
      ph_pro_check_price_input: "EUR価格（例：1200）…",
      ph_pro_check_result: "上のボタンを選択",
      ph_pro_order_destination: "上のボタンを選択",
      ph_pro_order_brand: "ブランド名または商品URL…",
      ph_pro_order_confirm_product: "上のボタンを選択",
      ph_pro_order_brand_manual: "ブランド名…",
      ph_pro_order_product_desc: "商品説明またはURL…",
      ph_pro_order_size: "上でサイズを選択",
      ph_pro_order_size_manual: "サイズを入力…",
      ph_pro_order_color: "上で色を選択",
      ph_pro_order_color_manual: "色を入力…",
      ph_pro_order_quantity: "数量（例: 1）…",
      ph_pro_order_product_notes: "この商品の詳細…",
      ph_pro_order_add_more: "上のボタンを選択",
      ph_pro_order_client_name: "顧客のフルネーム…",
      ph_pro_order_client_address: "1行で住所全体…",
      ph_pro_order_client_postal_code: "郵便番号…",
      ph_pro_order_client_phone: "顧客の電話番号…",
      ph_pro_order_client_customs_id: "P-XXXXXXXXXXXX",
      ph_pro_order_client_notes: "追加情報（任意）…",
      ph_pro_order_review: "上のボタンを選択",
      ph_default:         "メッセージを入力…",
    },
    "zh-hk": {
      hero_tagline:    "個人代購服務",
      select:          "選擇",
      confirm_send:    "確認並送出",
      add_another:     "新增其他產品",
      finish:          "檢視並完成",
      none_of_these:   "以上皆非",
      yes:             "是的，正確",
      no_change:       "不，我要更改",
      skip:            "跳過",
      edit_last:       "編輯上一個產品",
      remove_last:     "刪除上一個產品",
      view_ref:        "查看官方參考",
      step_label:      function (s, t) { return "步驟 " + s + " / " + t; },
      summary_eyebrow: "請求摘要",
      product_prefix:  "產品 ",
      confirm_ref:     "REF",
      confirm_msg:     "已收到您的請求，我們的團隊將盡快與您聯繫。",
      confirm_submitted: "請求已送出",
      ph_ask_first_name:  "你的名字…",
      ph_ask_email:       "電子郵件地址…",
      ph_confirm_email:   "請點選上方按鈕",
      ph_ask_market:      "請點選上方按鈕",
      ph_ask_brand:       "品牌名稱…",
      ph_ask_product_input: "描述產品…",
      ph_present_suggestions: "請點選上方卡片",
      ph_ask_color:       "顏色偏好…",
      ph_ask_size:        "尺寸（如：42、M）…",
      ph_ask_notes:       "補充備註…",
      ph_recap:           "請點選上方按鈕",
      ph_welcome:         "你的名字…",
      ph_welcome_menu:    "輸入訊息或點擊按鈕…",
      ph_qa_chat:         "請輸入你的問題…",
      ph_ask_pro_code:    "點擊上方按鈕",
      ph_enter_pro_code:  "你的訪問代碼…",
      ph_pro_reg_name:    "你的全名…",
      ph_pro_reg_company: "公司名稱…",
      ph_pro_reg_email:   "專業電郵…",
      ph_pro_reg_market:  "點擊上方按鈕",
      ph_pro_reg_address: "商業地址…",
      ph_pro_reg_phone:   "電話號碼…",
      ph_pro_reg_website: "你的網站URL…",
      ph_pro_reg_done:    "點擊上方按鈕",
      ph_ps_entry:        "點擊上方按鈕",
      ph_ps_enter_code:   "訪問代碼…",
      ph_ps_browse:       "點擊上方產品",
      private_sale:       "私人特賣",
      ps_have_code:       "我有代碼",
      ps_no_code:         "如何獲取代碼？",
      ph_pro_order_menu:  "點擊上方按鈕",
      ph_pro_check_brand: "品牌名稱…",
      ph_pro_check_price_input: "EUR 價格（例如 1200）…",
      ph_pro_check_result: "點擊上方按鈕",
      ph_pro_order_destination: "點擊上方按鈕",
      ph_pro_order_brand: "品牌名稱或產品網址…",
      ph_pro_order_confirm_product: "點擊上方按鈕",
      ph_pro_order_brand_manual: "品牌名稱…",
      ph_pro_order_product_desc: "產品描述或URL…",
      ph_pro_order_size: "點擊上方選擇尺寸",
      ph_pro_order_size_manual: "輸入尺寸…",
      ph_pro_order_color: "點擊上方選擇顏色",
      ph_pro_order_color_manual: "輸入顏色…",
      ph_pro_order_quantity: "數量（如：1）…",
      ph_pro_order_product_notes: "此產品的特別說明…",
      ph_pro_order_add_more: "點擊上方按鈕",
      ph_pro_order_client_name: "客戶全名…",
      ph_pro_order_client_address: "一行寫完整地址…",
      ph_pro_order_client_postal_code: "郵遞區號…",
      ph_pro_order_client_phone: "客戶電話號碼…",
      ph_pro_order_client_customs_id: "P-XXXXXXXXXXXX",
      ph_pro_order_client_notes: "補充資訊（可選）…",
      ph_pro_order_review: "點擊上方按鈕",
      ph_default:         "請輸入訊息…",
    },
    th: {
      hero_tagline:    "บริการจัดหาสินค้าส่วนตัว",
      select:          "เลือก",
      confirm_send:    "ยืนยันและส่ง",
      add_another:     "เพิ่มสินค้าอีกชิ้น",
      finish:          "ตรวจสอบและเสร็จสิ้น",
      none_of_these:   "ไม่ใช่รายการเหล่านี้",
      yes:             "ใช่ ถูกต้อง",
      no_change:       "ไม่ เปลี่ยน",
      skip:            "ข้าม",
      edit_last:       "แก้ไขสินค้าล่าสุด",
      remove_last:     "ลบสินค้าล่าสุด",
      view_ref:        "ดูข้อมูลอ้างอิงอย่างเป็นทางการ",
      step_label:      function (s, t) { return "ขั้นตอน " + s + " / " + t; },
      summary_eyebrow: "สรุปคำขอ",
      product_prefix:  "สินค้า ",
      confirm_ref:     "REF",
      confirm_msg:     "เราได้รับคำขอของคุณแล้ว ทีมงานจะติดต่อกลับในเร็ว ๆ นี้",
      confirm_submitted: "ส่งคำขอแล้ว",
      ph_ask_first_name:  "ชื่อของคุณ…",
      ph_ask_email:       "อีเมลของคุณ…",
      ph_confirm_email:   "แตะปุ่มด้านบน",
      ph_ask_market:      "แตะปุ่มด้านบน",
      ph_ask_brand:       "ชื่อแบรนด์…",
      ph_ask_product_input: "อธิบายสินค้า…",
      ph_present_suggestions: "แตะการ์ดด้านบน",
      ph_ask_color:       "สีที่ต้องการ…",
      ph_ask_size:        "ไซส์ (เช่น 42, M, OS)…",
      ph_ask_notes:       "หมายเหตุพิเศษ…",
      ph_recap:           "แตะปุ่มด้านบน",
      ph_welcome:         "ชื่อของคุณ…",
      ph_welcome_menu:    "พิมพ์ข้อความหรือแตะปุ่ม…",
      ph_qa_chat:         "ถามคำถาม…",
      ph_ask_pro_code:    "แตะปุ่มด้านบน",
      ph_enter_pro_code:  "รหัสเข้าใช้ของคุณ…",
      ph_pro_reg_name:    "ชื่อเต็ม…",
      ph_pro_reg_company: "ชื่อบริษัท…",
      ph_pro_reg_email:   "อีเมลธุรกิจ…",
      ph_pro_reg_market:  "แตะปุ่มด้านบน",
      ph_pro_reg_address: "ที่อยู่ธุรกิจ…",
      ph_pro_reg_phone:   "หมายเลขโทรศัพท์…",
      ph_pro_reg_website: "URL เว็บไซต์ของคุณ…",
      ph_pro_reg_done:    "แตะปุ่มด้านบน",
      ph_ps_entry:        "แตะปุ่มด้านบน",
      ph_ps_enter_code:   "รหัสเข้าใช้…",
      ph_ps_browse:       "แตะสินค้าด้านบน",
      private_sale:       "Private Sale",
      ps_have_code:       "ฉันมีรหัส",
      ps_no_code:         "วิธีรับรหัส",
      ph_pro_order_menu:  "แตะปุ่มด้านบน",
      ph_pro_check_brand: "ชื่อแบรนด์…",
      ph_pro_check_price_input: "ราคา EUR (เช่น 1200)…",
      ph_pro_check_result: "แตะปุ่มด้านบน",
      ph_pro_order_destination: "แตะปุ่มด้านบน",
      ph_pro_order_brand: "ชื่อแบรนด์หรือ URL สินค้า…",
      ph_pro_order_confirm_product: "แตะปุ่มด้านบน",
      ph_pro_order_brand_manual: "ชื่อแบรนด์…",
      ph_pro_order_product_desc: "คำอธิบายสินค้าหรือ URL…",
      ph_pro_order_size: "แตะเพื่อเลือกไซส์",
      ph_pro_order_size_manual: "พิมพ์ไซส์…",
      ph_pro_order_color: "แตะเพื่อเลือกสี",
      ph_pro_order_color_manual: "พิมพ์สี…",
      ph_pro_order_quantity: "จำนวน (เช่น 1)…",
      ph_pro_order_product_notes: "รายละเอียดพิเศษสำหรับสินค้านี้…",
      ph_pro_order_add_more: "แตะปุ่มด้านบน",
      ph_pro_order_client_name: "ชื่อเต็มของลูกค้า…",
      ph_pro_order_client_address: "เขียนที่อยู่ทั้งหมดในบรรทัดเดียว…",
      ph_pro_order_client_postal_code: "รหัสไปรษณีย์…",
      ph_pro_order_client_phone: "เบอร์โทรศัพท์ของลูกค้า…",
      ph_pro_order_client_customs_id: "P-XXXXXXXXXXXX",
      ph_pro_order_client_notes: "ข้อมูลเพิ่มเติม (ไม่จำเป็น)…",
      ph_pro_order_review: "แตะปุ่มด้านบน",
      ph_default:         "พิมพ์ข้อความ…",
    },
  };

  function t(locale, key) {
    var dict = I18N[locale] || I18N.en;
    return dict[key] || (I18N.en[key] || key);
  }

  /* ========== API ========== */
  async function postJSON(url, body) {
    var r = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    var data = await r.json().catch(function () { return {}; });
    if (!r.ok) throw new Error(data && (data.assistant_message || data.error) || "Request failed.");
    return data;
  }

  /* ========== stage helpers ========== */
  function setStage(root, stage) { root.setAttribute("data-stage", stage || ""); }
  function getStage(root) { return root.getAttribute("data-stage") || ""; }
  function scrollToBottom(list) {
    requestAnimationFrame(function () {
      list.scrollTo({ top: list.scrollHeight, behavior: "smooth" });
    });
  }

  /* ========== visual tap feedback (works on iOS + Android) ========== */
  function tapFeedback(btn) {
    if (!btn) return;
    btn.classList.remove("sc--tapped");
    // Force reflow so re-adding the class retriggers animation
    void btn.offsetWidth;
    btn.classList.add("sc--tapped");
    btn.addEventListener("animationend", function handler() {
      btn.classList.remove("sc--tapped");
      btn.removeEventListener("animationend", handler);
    });
    // Also try vibrate on Android
    if (navigator.vibrate) {
      try { navigator.vibrate(10); } catch (_) {}
    }
  }

  /* ==========================================================
     DYNAMIC PLACEHOLDER — changes per stage
     ========================================================== */
  /* Les stages sa_* (relais « Add to my list » de la recherche dashboard)
     réutilisent placeholders + masques de saisie de leurs équivalents du flux
     principal — mêmes formats coréens : (010) XXXX-XXXX, P-XXXX-XXXX-XXXX. */
  var SA_STAGE_ALIAS = {
    sa_c_name: "pro_order_client_name",
    sa_c_zip: "pro_order_client_postal_code",
    sa_c_addr: "pro_order_client_address",
    sa_c_addr2: "pro_order_client_address2",
    sa_c_phone: "pro_order_client_phone",
    sa_c_pccc: "pro_order_client_customs_id",
    sa_qty: "pro_order_quantity",
    sa_qty_manual: "pro_order_quantity_manual",
    sa_size: "pro_order_size_manual",
    sa_color: "pro_order_color_manual",
  };
  function saAlias(stage) { return SA_STAGE_ALIAS[stage] || stage; }

  function updatePlaceholder(root, stage, locale) {
    var input = $(".sc-input", root);
    if (!input) return;
    var key = "ph_" + saAlias(stage);
    var dict = I18N[locale] || I18N.en;
    var ph = dict[key] || dict.ph_default || (I18N.en[key] || I18N.en.ph_default);
    input.setAttribute("placeholder", ph);
  }

  /* ==========================================================
     FLOATING CONTEXT BADGE — shows name / market / brand
     ========================================================== */
  var contextData = { name: "", market: "", brand: "" };

  function updateContextBadge(root) {
    var badge = $(".sc-context-badge", root);
    if (!badge) return;
    var chips = [];
    if (contextData.name)   chips.push(contextData.name);
    if (contextData.market) chips.push(contextData.market);
    if (contextData.brand)  chips.push(contextData.brand);
    if (chips.length === 0) {
      badge.classList.remove("sc-context-badge--visible");
      return;
    }
    badge.classList.add("sc-context-badge--visible");
    var html = '<div class="sc-context-badge__items">';
    chips.forEach(function (c, i) {
      if (i > 0) html += '<span class="sc-context-badge__sep">·</span>';
      html += '<span class="sc-context-badge__chip">' + escapeHtml(c) + '</span>';
    });
    html += '</div>';
    badge.innerHTML = html;
  }

  // Called after each API response to extract context from state
  function extractContext(data) {
    if (!data || !data.state) return;
    var s = data.state;
    if (s.first_name) contextData.name = s.first_name;
    if (s.market)     contextData.market = s.market;
    if (s.brand)      contextData.brand = s.brand;
  }

  /* ==========================================================
     BRAND AUTOCOMPLETE
     ========================================================== */
  /* ========== HIGH-DEMAND BRANDS — warning on selection ========== */
  var HIGH_DEMAND_BRANDS = [
    // Latin / French
    "louis vuitton", "hermès", "hermes", "goyard",
    // Korean (한국어)
    "루이비통", "루이 비통", "에르메스", "헤르메스", "고야드",
    // Japanese (日本語)
    "ルイヴィトン", "ルイ・ヴィトン", "ルイビトン", "エルメス", "ゴヤール",
    // Chinese Traditional (繁體中文)
    "路易威登", "愛馬仕", "戈雅", "戈雅德",
    // Thai (ไทย)
    "หลุยส์วิตตอง", "หลุยส์ วิตตอง", "แอร์เมส", "โกยาร์ด",
    // Common abbreviations / misspellings
    "lv", "vuitton",
  ];

  var BRAND_WARNING_I18N = {
    en: function (brand) {
      return "⚠ You have selected " + brand + ". Please note: due to the very high demand and the complexity of sourcing these products, we cannot guarantee the fulfilment of your request. Prices may also be significantly higher than average. You may still submit your request — please monitor your emails carefully for our follow-up.";
    },
    fr: function (brand) {
      return "⚠ Vous avez choisi la marque " + brand + ". Attention : en raison de la très forte demande et de la complexité d'achat de ces produits, nous ne sommes pas en mesure de garantir l'aboutissement de votre demande. Les prix risquent également d'être plus élevés que la moyenne. Vous pouvez tout de même soumettre votre demande — veuillez surveiller vos emails pour notre retour.";
    },
    ko: function (brand) {
      return "⚠ " + brand + " 브랜드를 선택하셨습니다. 참고: 해당 제품은 수요가 매우 높고 구매 과정이 복잡하여 요청이 성사되지 않을 수 있습니다. 또한 가격이 평균보다 상당히 높을 수 있습니다. 그래도 요청을 제출하실 수 있으며, 이메일을 통해 결과를 확인해 주세요.";
    },
    ja: function (brand) {
      return "⚠ " + brand + " を選択されました。ご注意：この商品は需要が非常に高く、入手が困難なため、ご依頼にお応えできない場合がございます。また、価格が通常より大幅に高くなる可能性があります。ご依頼は引き続きお受けいたしますが、結果についてはメールをご確認ください。";
    },
    "zh-hk": function (brand) {
      return "⚠ 你已選擇 " + brand + "。請注意：由於需求非常高且採購過程複雜，我們無法保證你的請求一定能完成。價格也可能遠高於平均水平。你仍然可以提交請求 — 請留意你的電郵以獲取我們的回覆。";
    },
    th: function (brand) {
      return "⚠ คุณเลือกแบรนด์ " + brand + " โปรดทราบ: เนื่องจากความต้องการสูงมากและความซับซ้อนในการจัดหาสินค้าเหล่านี้ เราไม่สามารถรับประกันว่าคำขอของคุณจะสำเร็จ ราคาอาจสูงกว่าค่าเฉลี่ยอย่างมาก คุณยังสามารถส่งคำขอได้ — กรุณาตรวจสอบอีเมลของคุณสำหรับการติดตามผล";
    },
  };

  function getBrandWarning(brandName, locale) {
    var normalized = (brandName || "").toLowerCase().trim();
    if (!normalized) return null;
    var isHighDemand = HIGH_DEMAND_BRANDS.some(function (b) {
      // Short terms (≤3 chars like "lv") → exact match only
      if (b.length <= 3) return normalized === b;
      // Longer terms → substring match (covers typos, extra spaces, etc.)
      return normalized === b || normalized.includes(b);
    });
    if (!isHighDemand) return null;
    var fn = BRAND_WARNING_I18N[locale] || BRAND_WARNING_I18N.en;
    return fn(brandName);
  }

  /* ========== IMAGE DISCLAIMER for hard-to-scrape brands ========== */
  var IMAGE_DISCLAIMER_I18N = {
    en: "The images shown may not exactly match the product you requested. Don't worry — we have saved the link you provided and it is what will be used to process your order.",
    fr: "Les images affichées peuvent ne pas correspondre exactement au produit que vous avez demandé. Ne vous inquiétez pas — nous avons bien enregistré le lien que vous avez fourni et c'est celui-ci qui sera utilisé pour traiter votre demande.",
    ko: "표시된 이미지가 요청하신 제품과 정확히 일치하지 않을 수 있습니다. 걱정하지 마세요 — 제공해 주신 링크가 정확히 저장되었으며, 해당 링크를 기반으로 주문이 처리됩니다.",
    ja: "表示されている画像は、ご依頼の商品と異なる場合がございます。ご安心ください — お送りいただいたリンクは正確に保存されており、そのリンクをもとにご注文を処理いたします。",
    "zh-hk": "顯示的圖片可能與你要求的產品不完全相符。請放心 — 我們已保存你提供的連結，你的訂單將以該連結為準進行處理。",
    th: "รูปภาพที่แสดงอาจไม่ตรงกับสินค้าที่คุณต้องการทุกประการ ไม่ต้องกังวล — เราได้บันทึกลิงก์ที่คุณให้ไว้เรียบร้อยแล้ว และจะใช้ลิงก์นั้นในการดำเนินการคำสั่งซื้อของคุณ",
  };

  function getImageDisclaimer(locale) {
    return IMAGE_DISCLAIMER_I18N[locale] || IMAGE_DISCLAIMER_I18N.en;
  }

  var LUXURY_BRANDS = [
    "Hermès", "Louis Vuitton", "Chanel", "Dior", "Gucci",
    "Prada", "Bottega Veneta", "Saint Laurent", "Balenciaga", "Celine",
    "Loewe", "Fendi", "Burberry", "Givenchy", "Valentino",
    "Miu Miu", "Alexander McQueen", "Tom Ford", "Balmain", "Versace",
    "Cartier", "Van Cleef & Arpels", "Tiffany & Co.", "Bulgari", "Chopard",
    "Rolex", "Patek Philippe", "Audemars Piguet", "Omega", "IWC",
    "Goyard", "Rimowa", "Moynat", "Berluti", "Brunello Cucinelli",
    "Loro Piana", "Zegna", "Moncler", "Stone Island", "Maison Margiela",
    "Rick Owens", "Chrome Hearts", "Off-White", "Amiri", "Fear of God",
    "Nike", "Adidas", "New Balance", "Asics", "Salomon",
    "The North Face", "Arc'teryx", "Canada Goose",
    "Apple", "Samsung", "Sony", "Dyson", "Bang & Olufsen",
  ];

  function createBrandAutocomplete(root) {
    var input = $(".sc-input", root);
    var dropdown = $(".sc-autocomplete", root);
    if (!input || !dropdown) return { destroy: function () {}, isOpen: function () { return false; } };

    var activeIdx = -1;
    var matches = [];
    var isVisible = false;

    function render(query) {
      var q = (query || "").toLowerCase().trim();
      if (!q || q.length < 1) { hide(); return; }
      matches = LUXURY_BRANDS.filter(function (b) {
        return b.toLowerCase().includes(q);
      }).slice(0, 6);
      if (matches.length === 0) { hide(); return; }

      activeIdx = -1;
      dropdown.innerHTML = "";
      matches.forEach(function (brand, i) {
        var item = el("div", { class: "sc-autocomplete__item" });
        // Highlight matching part
        var lc = brand.toLowerCase();
        var idx = lc.indexOf(q);
        if (idx >= 0) {
          item.innerHTML = escapeHtml(brand.slice(0, idx))
            + "<mark>" + escapeHtml(brand.slice(idx, idx + q.length)) + "</mark>"
            + escapeHtml(brand.slice(idx + q.length));
        } else {
          item.textContent = brand;
        }
        item.addEventListener("mousedown", function (e) {
          e.preventDefault(); // prevent blur
          input.value = brand;
          hide();
          // Trigger send
          var sendBtn = $(".sc-send", root);
          if (sendBtn) sendBtn.click();
        });
        dropdown.appendChild(item);
      });
      dropdown.classList.add("sc-autocomplete--open");
      isVisible = true;
    }

    function hide() {
      dropdown.classList.remove("sc-autocomplete--open");
      dropdown.innerHTML = "";
      isVisible = false;
      activeIdx = -1;
      matches = [];
    }

    function onInput() {
      var stage = root.getAttribute("data-stage") || "";
      if (stage !== "ask_brand") { hide(); return; }
      render(input.value);
    }

    function onKeydown(e) {
      if (!isVisible) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        activeIdx = Math.min(activeIdx + 1, matches.length - 1);
        updateActive();
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        activeIdx = Math.max(activeIdx - 1, 0);
        updateActive();
      } else if (e.key === "Enter" && activeIdx >= 0) {
        e.preventDefault();
        input.value = matches[activeIdx];
        hide();
        var sendBtn = $(".sc-send", root);
        if (sendBtn) sendBtn.click();
      } else if (e.key === "Escape") {
        hide();
      }
    }

    function updateActive() {
      var items = dropdown.querySelectorAll(".sc-autocomplete__item");
      items.forEach(function (it, i) {
        it.classList.toggle("sc-autocomplete__item--active", i === activeIdx);
      });
    }

    function onBlur() {
      setTimeout(hide, 150);
    }

    input.addEventListener("input", onInput);
    input.addEventListener("keydown", onKeydown);
    input.addEventListener("blur", onBlur);

    return {
      destroy: function () {
        input.removeEventListener("input", onInput);
        input.removeEventListener("keydown", onKeydown);
        input.removeEventListener("blur", onBlur);
      },
      isOpen: function () { return isVisible; }
    };
  }

  /* ==========================================================
     PROGRESS BAR
     ========================================================== */
  var STAGE_STEPS = {
    // Standard product request flow (name already collected at welcome)
    ask_first_name:       { step: 1, total: 5 },
    ask_email:            { step: 1, total: 5 },
    confirm_email:        { step: 1, total: 5 },
    ask_market:           { step: 2, total: 5 },
    ask_brand:            { step: 3, total: 5 },
    ask_product_input:    { step: 3, total: 5 },
    present_suggestions:  { step: 3, total: 5 },
    confirm_link_product: { step: 3, total: 5 },
    ask_color:            { step: 4, total: 5 },
    ask_size:             { step: 4, total: 5 },
    ask_notes:            { step: 4, total: 5 },
    after_product:        { step: 4, total: 5 },
    recap:                { step: 5, total: 5 },
    submitted:            { step: 5, total: 5 },
    // Pro registration flow
    pro_reg_name:         { step: 1, total: 6 },
    pro_reg_company:      { step: 2, total: 6 },
    pro_reg_email:        { step: 3, total: 6 },
    pro_reg_market:       { step: 3, total: 6 },
    pro_reg_address:      { step: 4, total: 6 },
    pro_reg_phone:        { step: 5, total: 6 },
    pro_reg_website:      { step: 5, total: 6 },
    pro_reg_done:         { step: 6, total: 6 },
    // Private sale flow
    ps_entry:             { step: 1, total: 2 },
    ps_enter_code:        { step: 1, total: 2 },
    ps_browse:            { step: 2, total: 2 },
    // Pro order flow — check a price
    pro_check_brand:       { step: 1, total: 2 },
    pro_check_price_input: { step: 2, total: 2 },
    pro_check_result:      { step: 2, total: 2 },
    // Pro order flow — create an order
    pro_order_menu:       { step: 1, total: 5 },
    pro_order_destination:   { step: 1, total: 5 },
    pro_order_brand:      { step: 2, total: 5 },
    pro_order_confirm_product: { step: 2, total: 5 },
    pro_order_brand_manual:    { step: 2, total: 5 },
    pro_order_product_desc:  { step: 2, total: 5 },
    pro_order_size:          { step: 2, total: 5 },
    pro_order_size_manual:   { step: 2, total: 5 },
    pro_order_color:         { step: 2, total: 5 },
    pro_order_color_manual:  { step: 2, total: 5 },
    pro_order_quantity:      { step: 3, total: 5 },
    pro_order_product_notes: { step: 3, total: 5 },
    pro_order_add_more:      { step: 3, total: 5 },
    pro_order_client_name:   { step: 4, total: 5 },
    pro_order_client_address:     { step: 4, total: 5 },
    pro_order_client_postal_code: { step: 4, total: 5 },
    pro_order_client_phone:       { step: 4, total: 5 },
    pro_order_client_customs_id:  { step: 4, total: 5 },
    pro_order_client_notes:       { step: 4, total: 5 },
    pro_order_review:        { step: 5, total: 5 },
    pro_order_submitted:     { step: 5, total: 5 },
    // welcome, qa_chat, ask_pro_code → no progress bar (handled by default)
  };

  function updateProgressBar(root, stage, locale) {
    var fill  = $(".sc-progress__fill", root);
    var label = $(".sc-progress__label", root);
    var head  = $(".sc-concierge__head", root);
    if (!fill) return;
    var info = STAGE_STEPS[stage];
    if (!info) {
      fill.style.width = "0%";
      if (label) label.textContent = "";
      if (head) head.classList.remove("sc-progress--active");
      return;
    }
    var pct = Math.round((info.step / info.total) * 100);
    fill.style.width = pct + "%";
    var stepFn = t(locale, "step_label");
    if (label) label.textContent = typeof stepFn === "function" ? stepFn(info.step, info.total) : "";
    if (head) head.classList.add("sc-progress--active");
  }

  /* ==========================================================
     CONFIRMATION CARD — after submit (fully i18n)
     ========================================================== */
  function renderConfirmationCard(list, requestId, locale) {
    var card = el("div", { class: "sc-confirmation" });

    var check = el("div", { class: "sc-confirmation__check" });
    check.innerHTML = '<svg viewBox="0 0 24 24"><path d="M5 12l5 5L19 7" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    card.appendChild(check);

    var refText = requestId
      ? (t(locale, "confirm_ref") + " " + requestId)
      : t(locale, "confirm_submitted");
    card.appendChild(el("div", { class: "sc-confirmation__ref", text: refText }));
    card.appendChild(el("div", { class: "sc-confirmation__message", text: t(locale, "confirm_msg") }));

    list.appendChild(card);
    scrollToBottom(list);
  }

  /* ==========================================================
     PLATFORM LOGOS (visual marketplace indicators)
     ========================================================== */
  function renderPlatformLogos(list, logos) {
    var wrap = el("div", { class: "sc-platform-logos" });
    logos.forEach(function(logo) {
      var img = document.createElement("img");
      img.src = logo.url;
      img.alt = logo.name;
      img.title = logo.name;
      img.className = "sc-platform-logo";
      img.onerror = function() { this.style.display = "none"; };
      wrap.appendChild(img);
    });
    list.appendChild(wrap);
    scrollToBottom(list);
  }

  /* ==========================================================
     INPUT FORMATTING (KR phone & PCCC live masks)
     ========================================================== */
  var activeInputFormatter = null;
  function setupInputFormatting(root, stage, market) {
    var inp = $(".sc-input", root);
    if (!inp) return;
    stage = saAlias(stage);
    // Remove previous formatter
    if (activeInputFormatter) {
      inp.removeEventListener("input", activeInputFormatter);
      activeInputFormatter = null;
      inp.removeAttribute("maxlength");
      inp.type = "text";
    }
    // Korean phone: (010) XXXX-XXXX
    if (stage === "pro_order_client_phone" && market === "KR") {
      inp.type = "tel";
      inp.setAttribute("maxlength", "16");
      activeInputFormatter = function() {
        var raw = inp.value.replace(/\D/g, "");
        if (!raw) { inp.value = ""; return; }
        var f = "";
        if (raw.length <= 3) f = "(" + raw;
        else if (raw.length <= 7) f = "(" + raw.slice(0,3) + ") " + raw.slice(3);
        else f = "(" + raw.slice(0,3) + ") " + raw.slice(3,7) + "-" + raw.slice(7,11);
        inp.value = f;
      };
      inp.addEventListener("input", activeInputFormatter);
    }
    // Korean Business Registration Number: XXX-XX-XXXXX (10 digits)
    else if (stage === "pro_signup_regnum" && market === "KR") {
      inp.type = "tel";
      inp.setAttribute("maxlength", "12");
      activeInputFormatter = function() {
        var raw = inp.value.replace(/\D/g, "").slice(0, 10);
        if (!raw) { inp.value = ""; return; }
        var f = "";
        if (raw.length <= 3) f = raw;
        else if (raw.length <= 5) f = raw.slice(0,3) + "-" + raw.slice(3);
        else f = raw.slice(0,3) + "-" + raw.slice(3,5) + "-" + raw.slice(5);
        inp.value = f;
      };
      inp.addEventListener("input", activeInputFormatter);
    }
    // KR zip: 5 digits exactly — the input physically can't take more
    else if (stage === "pro_order_client_postal_code" && market === "KR") {
      inp.type = "tel";
      inp.setAttribute("maxlength", "5");
      activeInputFormatter = function() {
        inp.value = inp.value.replace(/\D/g, "").slice(0, 5);
      };
      inp.addEventListener("input", activeInputFormatter);
    }
    // PCCC: P-XXXX-XXXX-XXXX (P fixed + 12 digits)
    else if (stage === "pro_order_client_customs_id") {
      inp.setAttribute("maxlength", "16");
      activeInputFormatter = function() {
        var raw = inp.value.replace(/[^0-9Pp]/g, "");
        if (raw.length > 0 && raw[0].toUpperCase() !== "P") raw = "P" + raw;
        if (!raw) { inp.value = ""; return; }
        var digits = raw.slice(1).replace(/\D/g, "").slice(0, 12);
        var f = "P";
        if (digits.length <= 4) f = "P-" + digits;
        else if (digits.length <= 8) f = "P-" + digits.slice(0,4) + "-" + digits.slice(4);
        else f = "P-" + digits.slice(0,4) + "-" + digits.slice(4,8) + "-" + digits.slice(8);
        inp.value = f;
      };
      inp.addEventListener("input", activeInputFormatter);
    }
  }

  /* ==========================================================
     FULLSCREEN MANAGER
     ========================================================== */
  function createFullscreenManager(section, root) {
    var isFullscreen = false;
    var head = $(".sc-concierge__head", root);
    var closeBtn = $(".sc-close", root);
    var list = $(".sc-list", root);
    var lastScrollTop = 0;
    var headHidden = false;
    var savedScrollY = 0;

    function setVH() {
      var vh = (window.visualViewport ? window.visualViewport.height : window.innerHeight) * 0.01;
      section.style.setProperty("--sc-vh", vh + "px");
    }

    function enterFullscreen() {
      if (isFullscreen || !isMobile()) return;
      isFullscreen = true;
      savedScrollY = window.scrollY || window.pageYOffset || 0;
      section.classList.add("sc--fullscreen");
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
      document.body.style.top = -savedScrollY + "px";
      document.body.style.left = "0";
      setVH();

      // Force synchronous layout computation so the browser commits
      // the fullscreen flex layout BEFORE the keyboard manager's
      // direct syncLayout reads visualViewport values.
      // Without this, iOS Safari applies kb-open styles on an
      // uncommitted flex layout, causing the inputrow to appear at top.
      void section.offsetHeight;

      // Double-rAF: wait for the browser to paint the fullscreen layout,
      // then scroll. This gives iOS Safari an extra frame to settle.
      requestAnimationFrame(function () {
        requestAnimationFrame(function () { scrollToBottom(list); });
      });
    }

    function exitFullscreen() {
      if (!isFullscreen) return;
      isFullscreen = false;
      section.classList.remove("sc--fullscreen");
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
      document.body.style.top = "";
      document.body.style.left = "";
      window.scrollTo(0, savedScrollY);
      if (head) { head.classList.remove("sc-head--hidden"); headHidden = false; }
    }

    if (closeBtn) {
      closeBtn.addEventListener("click", function (e) {
        e.stopPropagation();
        tapFeedback(closeBtn);
        exitFullscreen();
      });
    }

    // Header hide on scroll
    if (list && head) {
      list.addEventListener("scroll", function () {
        if (!isFullscreen) return;
        var st = list.scrollTop;
        if (st > lastScrollTop + 12 && st > 60) {
          if (!headHidden) { head.classList.add("sc-head--hidden"); headHidden = true; }
        } else if (st < lastScrollTop - 8) {
          if (headHidden) { head.classList.remove("sc-head--hidden"); headHidden = false; }
        }
        lastScrollTop = Math.max(0, st);
      }, { passive: true });
    }

    // First interaction triggers fullscreen
    var inputEl = $(".sc-input", root);
    var bodyEl  = $(".sc-body", root);
    if (inputEl) inputEl.addEventListener("focus", function () { enterFullscreen(); }, { once: true });
    if (bodyEl) {
      bodyEl.addEventListener("click", function handler(e) {
        if (e.target.closest(".sc-action, .sc-product-card__select, .sc-send, .sc-input, .sc-list")) {
          enterFullscreen();
          bodyEl.removeEventListener("click", handler);
        }
      });
    }

    window.addEventListener("resize", function () {
      if (!isMobile() && isFullscreen) exitFullscreen();
      if (isFullscreen) setVH();
    });
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", function () { if (isFullscreen) setVH(); });
    }

    return { get active() { return isFullscreen; }, enter: enterFullscreen, exit: exitFullscreen };
  }

  /* ==========================================================
     KEYBOARD MANAGER — resize the SECTION itself on iOS
     When the virtual keyboard opens on iOS Safari, the fixed
     container stays at full window height but the keyboard
     covers the bottom. We shrink the section to exactly
     visualViewport.height so the input bar stays above the
     keyboard — just like a native chat app.
     ========================================================== */
  function createKeyboardManager(section, root) {
    if (!window.visualViewport) return { destroy: function () {} };
    var list = $(".sc-list", root);
    var wrap = $(".sc-concierge__wrap", section);
    var rafId = 0;
    var wasKbOpen = false;

    // Capture the full-screen height ONCE as a stable baseline.
    // On iOS Safari, window.innerHeight can drift after keyboard
    // open/close cycles, breaking the diff-based detection.
    var baseHeight = 0;

    function onViewportChange() {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(function () {
        if (!section.classList.contains("sc--fullscreen")) return;

        // Lock the baseline on first run in fullscreen
        if (baseHeight === 0) baseHeight = window.innerHeight;

        var vvh  = window.visualViewport.height;
        var vvt  = window.visualViewport.offsetTop || 0;
        var diff = baseHeight - vvh;
        var kbOpen = diff > 80;

        if (kbOpen) {
          section.classList.add("sc--kb-open");
          section.style.height = vvh + "px";
          section.style.top    = vvt + "px";
          section.style.bottom = "auto";
          if (wrap) wrap.style.height = vvh + "px";
          if (!wasKbOpen) {
            // Force reflow on every closed→open transition so the
            // flex layout is committed before the browser paints.
            void section.offsetHeight;
            if (list) {
              requestAnimationFrame(function () {
                list.scrollTo({ top: list.scrollHeight, behavior: "auto" });
              });
            }
          }
          wasKbOpen = true;
        } else {
          section.classList.remove("sc--kb-open");
          section.style.height = "";
          section.style.top    = "0";
          section.style.bottom = "0";
          if (wrap) wrap.style.height = "";
          wasKbOpen = false;
        }
      });
    }

    window.visualViewport.addEventListener("resize", onViewportChange);
    window.visualViewport.addEventListener("scroll", onViewportChange);

    var input = $(".sc-input", root);
    function onInputFocus() {
      if (!section.classList.contains("sc--fullscreen")) return;
      // Burst checks at staggered timings to catch iOS keyboard
      // animation. Each one triggers a fresh rAF that reads the
      // latest visualViewport values.
      [50, 150, 300, 500, 700].forEach(function (delay) {
        setTimeout(onViewportChange, delay);
      });
    }
    if (input) {
      input.addEventListener("focus", onInputFocus);
    }

    return {
      destroy: function () {
        window.visualViewport.removeEventListener("resize", onViewportChange);
        window.visualViewport.removeEventListener("scroll", onViewportChange);
        if (input) input.removeEventListener("focus", onInputFocus);
      }
    };
  }

  /* ========== iOS zoom prevention ========== */
  function preventIOSZoom() {
    if (!isMobile()) return;
    var meta = document.querySelector('meta[name="viewport"]');
    if (meta) {
      var content = meta.getAttribute("content") || "";
      if (!content.includes("maximum-scale")) {
        meta.setAttribute("content", content + ", maximum-scale=1.0, user-scalable=no");
      }
    }
  }

  /* ==========================================================
     RENDER: messages
     ========================================================== */
  function renderMessage(list, who, text, extra) {
    var item = el("div", { class: "sc-msg sc-msg--" + who });
    if (who === "assistant") item.appendChild(el("div", { class: "sc-avatar" }));
    var bubble = el("div", { class: "sc-bubble" });
    // Check if there's a product image to show
    if (extra && extra.image_url) {
      var img = document.createElement("img");
      img.src = extra.image_url;
      img.className = "sc-price-badge__image";
      img.alt = "Product";
      img.onerror = function () { this.style.display = "none"; };
      bubble.appendChild(img);
    }
    bubble.innerHTML += linkifyText(text);
    item.appendChild(bubble);
    list.appendChild(item);
    scrollToBottom(list);
  }

  /**
   * Render an inline video preview in the chat (autoplay, muted, loop, no controls, no download).
   */
  function renderVideoPreview(list, videoUrl) {
    if (!videoUrl) return;
    var item = el("div", { class: "sc-msg sc-msg--assistant" });
    item.appendChild(el("div", { class: "sc-avatar" }));
    var bubble = el("div", { class: "sc-bubble" });
    var video = document.createElement("video");
    video.src = videoUrl;
    video.autoplay = true;
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.setAttribute("playsinline", "");
    video.setAttribute("webkit-playsinline", "");
    video.setAttribute("disablePictureInPicture", "");
    video.setAttribute("controlsList", "nodownload nofullscreen noremoteplayback");
    video.style.cssText = "width:100%;max-width:280px;border-radius:12px;display:block;pointer-events:none;";
    // Prevent right-click / context menu (no download)
    video.addEventListener("contextmenu", function(e) { e.preventDefault(); });
    bubble.appendChild(video);
    item.appendChild(bubble);
    list.appendChild(item);
    scrollToBottom(list);
  }

  /**
   * Render a price check result as a styled badge card.
   * @param {Element} list — chat message list
   * @param {Object} data — { brand, title, price, note, image_url }
   */
  function renderPriceBadge(list, data) {
    var item = el("div", { class: "sc-msg sc-msg--assistant" });
    item.appendChild(el("div", { class: "sc-avatar" }));
    var bubble = el("div", { class: "sc-bubble" });
    var badge = el("div", { class: "sc-price-badge" });
    // Brand logo (Shopify CDN) or fallback to brand name text
    if (data.logo_url) {
      var logo = document.createElement("img");
      logo.src = data.logo_url;
      logo.className = "sc-price-badge__logo";
      logo.alt = data.brand || "Brand";
      logo.onerror = function () {
        this.style.display = "none";
        // Show brand text as fallback
        var fallback = el("div", { class: "sc-price-badge__brand", text: data.brand || "" });
        badge.insertBefore(fallback, badge.firstChild);
      };
      badge.appendChild(logo);
    } else if (data.brand) {
      badge.appendChild(el("div", { class: "sc-price-badge__brand", text: data.brand }));
    }
    if (data.title) badge.appendChild(el("div", { class: "sc-price-badge__title", text: data.title }));
    // Divider line before price
    badge.appendChild(el("div", { class: "sc-price-badge__divider" }));
    if (data.price) badge.appendChild(el("div", { class: "sc-price-badge__price", text: data.price }));
    if (data.note) badge.appendChild(el("div", { class: "sc-price-badge__note", text: data.note }));
    if (data.rate_info) badge.appendChild(el("div", { class: "sc-price-badge__rate", text: data.rate_info }));
    bubble.appendChild(badge);
    item.appendChild(bubble);
    list.appendChild(item);
    scrollToBottom(list);
  }

  /**
   * Render product cards for order recap (with image, brand, title, size, color, qty).
   * @param {Element} list — chat message list
   * @param {Array} cards — array of { brand, title, image_url, size, color, quantity, notes }
   */
  function renderProductCards(list, cards) {
    if (!cards || !cards.length) return;

    // Group cards by client_name (empty string = no client / company)
    var groups = [];
    var currentGroup = null;
    cards.forEach(function(card) {
      var clientName = card.client_name || "";
      if (!currentGroup || currentGroup.name !== clientName) {
        currentGroup = { name: clientName, cards: [] };
        groups.push(currentGroup);
      }
      currentGroup.cards.push(card);
    });

    var hasMultipleClients = groups.length > 1 || (groups.length === 1 && groups[0].name);

    // ── Outer container: full-width, outside bubble ──
    var wrap = el("div", { class: "sc-recap-block" });

    if (hasMultipleClients) {
      // ═══ CLIENT-LEVEL CAROUSEL: each slide = 1 client with all products ═══
      var carousel = el("div", { class: "sc-recap-carousel" });

      groups.forEach(function(group, gi) {
        // One "slide" per client
        var slide = el("div", { class: "sc-recap-slide" });

        // Client header inside slide
        if (group.name) {
          slide.appendChild(el("div", { class: "sc-recap-slide__client", text: group.name }));
        }

        // Products list inside the slide
        group.cards.forEach(function(card) {
          var row = el("div", { class: "sc-recap-row" });

          // Product image (small thumbnail)
          var imgUrl = card.image_url || "";
          if (imgUrl) {
            var img = document.createElement("img");
            img.src = imgUrl;
            img.className = "sc-recap-row__img";
            img.alt = card.title || "Product";
            img.onerror = function() { this.style.display = "none"; };
            row.appendChild(img);
          }

          // Product info
          var info = el("div", { class: "sc-recap-row__info" });
          if (card.brand) info.appendChild(el("div", { class: "sc-recap-row__brand", text: card.brand }));
          if (card.title) info.appendChild(el("div", { class: "sc-recap-row__title", text: card.title }));
          var parts = [];
          if (card.quantity && card.quantity > 0) parts.push("Qty: " + card.quantity);
          if (card.size) parts.push(card.size);
          if (card.color) parts.push(card.color);
          if (parts.length) info.appendChild(el("div", { class: "sc-recap-row__details", text: parts.join(" · ") }));
          if (card.notes) info.appendChild(el("div", { class: "sc-recap-row__notes", text: card.notes }));
          if (card.url) {
            var link = document.createElement("a");
            link.href = card.url;
            link.target = "_blank";
            link.rel = "noopener";
            link.className = "sc-recap-row__url";
            link.textContent = "↗";
            info.appendChild(link);
          }

          row.appendChild(info);
          slide.appendChild(row);
        });

        // Slide counter
        slide.appendChild(el("div", { class: "sc-recap-slide__counter", text: (gi + 1) + " / " + groups.length }));

        carousel.appendChild(slide);
      });

      wrap.appendChild(carousel);
    } else {
      // ═══ SINGLE GROUP (company or 1 client): simple vertical layout ═══
      groups.forEach(function(group) {
        group.cards.forEach(function(card) {
          var row = el("div", { class: "sc-recap-row sc-recap-row--single" });

          var imgUrl = card.image_url || "";
          if (imgUrl) {
            var img = document.createElement("img");
            img.src = imgUrl;
            img.className = "sc-recap-row__img";
            img.alt = card.title || "Product";
            img.onerror = function() { this.style.display = "none"; };
            row.appendChild(img);
          }

          var info = el("div", { class: "sc-recap-row__info" });
          if (card.brand) info.appendChild(el("div", { class: "sc-recap-row__brand", text: card.brand }));
          if (card.title) info.appendChild(el("div", { class: "sc-recap-row__title", text: card.title }));
          var parts = [];
          if (card.quantity && card.quantity > 0) parts.push("Qty: " + card.quantity);
          if (card.size) parts.push(card.size);
          if (card.color) parts.push(card.color);
          if (parts.length) info.appendChild(el("div", { class: "sc-recap-row__details", text: parts.join(" · ") }));
          if (card.notes) info.appendChild(el("div", { class: "sc-recap-row__notes", text: card.notes }));
          if (card.url) {
            var link = document.createElement("a");
            link.href = card.url;
            link.target = "_blank";
            link.rel = "noopener";
            link.className = "sc-recap-row__url";
            link.textContent = "View product ↗";
            info.appendChild(link);
          }

          row.appendChild(info);
          wrap.appendChild(row);
        });
      });
    }

    list.appendChild(wrap);
    scrollToBottom(list);
  }

  function renderPromoBadge(list, text) {
    var item = el("div", { class: "sc-msg sc-msg--assistant" });
    item.appendChild(el("div", { class: "sc-avatar" }));
    var bubble = el("div", { class: "sc-bubble" });
    var badge = el("div", { class: "sc-promo-badge" });
    var check = el("div", { class: "sc-promo-badge__check" });
    check.innerHTML = '<svg viewBox="0 0 16 16"><path d="M3.5 8.5l3 3 6-7" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    badge.appendChild(check);
    // Remove the ✓ prefix if present since we have the visual checkmark
    var cleanText = text.replace(/^✓\s*/, "");
    badge.appendChild(el("span", { class: "sc-promo-badge__text", text: cleanText }));
    bubble.appendChild(badge);
    item.appendChild(bubble);
    list.appendChild(item);
    scrollToBottom(list);
    // Haptic feedback if available
    if (window.navigator && window.navigator.vibrate) {
      try { window.navigator.vibrate([30, 50, 30]); } catch (e) {}
    }
  }

  /* ========== RENDER: high-demand brand warning banner ========== */
  function renderBrandWarning(list, warningText) {
    var item = el("div", { class: "sc-msg sc-msg--assistant" });
    item.appendChild(el("div", { class: "sc-avatar" }));
    var bubble = el("div", { class: "sc-bubble sc-bubble--brand-warning" });
    var banner = el("div", { class: "sc-brand-warning" });
    var icon = el("div", { class: "sc-brand-warning__icon" });
    icon.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>';
    banner.appendChild(icon);
    banner.appendChild(el("div", { class: "sc-brand-warning__text", text: warningText }));
    bubble.appendChild(banner);
    item.appendChild(bubble);
    list.appendChild(item);
    scrollToBottom(list);
    // Haptic feedback for warning
    if (window.navigator && window.navigator.vibrate) {
      try { window.navigator.vibrate([50, 30, 50]); } catch (e) {}
    }
  }

  /* ========== RENDER: image disclaimer note below product cards ========== */
  function renderImageDisclaimer(container, locale) {
    var note = el("div", { class: "sc-image-disclaimer" });
    note.innerHTML = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="8" cy="8" r="7"/><line x1="8" y1="7" x2="8" y2="11"/><circle cx="8" cy="5" r="0.5" fill="currentColor" stroke="none"/></svg>'
      + '<span>' + escapeHtml(getImageDisclaimer(locale)) + '</span>';
    container.appendChild(note);
  }

  function showTyping(list) {
    var item = el("div", { class: "sc-msg sc-msg--assistant sc-msg--typing", "data-typing": "true" });
    item.appendChild(el("div", { class: "sc-avatar" }));
    var bubble = el("div", { class: "sc-bubble sc-bubble--typing" });
    var dots = el("div", { class: "sc-typing" });
    dots.appendChild(el("span", { class: "sc-typing__dot" }));
    dots.appendChild(el("span", { class: "sc-typing__dot" }));
    dots.appendChild(el("span", { class: "sc-typing__dot" }));
    bubble.appendChild(dots);
    item.appendChild(bubble);
    list.appendChild(item);
    scrollToBottom(list);
    return item;
  }

  function removeTyping(node) {
    if (node && node.parentNode) node.parentNode.removeChild(node);
  }

  /* ========== RENDER: image gallery ========== */
  function renderGallery(images, classPrefix) {
    var gallery = el("div", { class: classPrefix + "__gallery" });
    (images || []).slice(0, 3).forEach(function (src) {
      var media = el("div", { class: classPrefix + "__media" });
      var img = el("img", {
        class: classPrefix + "__img",
        src: src, alt: "Product image", loading: "lazy",
      });
      img.onerror = function () { media.style.display = "none"; };
      media.appendChild(img);
      gallery.appendChild(media);
    });
    return gallery;
  }

  /* ========== RENDER: summary panel (i18n) ========== */
  function renderSummary(summaryWrap, summary, locale) {
    summaryWrap.innerHTML = "";
    if (!summary || !summary.products || !summary.products.length) {
      summaryWrap.style.display = "none"; return;
    }
    summaryWrap.style.display = "block";
    var panel = el("div", { class: "sc-summary-panel" });
    var head  = el("div", { class: "sc-summary-head" });
    head.appendChild(el("div", { class: "sc-summary-eyebrow", text: t(locale, "summary_eyebrow") }));
    var meta = el("div", { class: "sc-summary-meta" }, [
      el("span", { class: "sc-summary-chip", text: summary.contact.name || "—" }),
      el("span", { class: "sc-summary-chip", text: summary.contact.email || "—" }),
      el("span", { class: "sc-summary-chip", text: summary.contact.market || "—" }),
    ]);
    head.appendChild(meta);
    panel.appendChild(head);

    var grid = el("div", { class: "sc-summary-grid" });
    summary.products.forEach(function (product) {
      var card = el("div", { class: "sc-summary-card" });
      var top  = el("div", { class: "sc-summary-card__top" });
      if (product.images && product.images.length) top.appendChild(renderGallery(product.images, "sc-summary-card"));
      var body = el("div", { class: "sc-summary-card__body" });
      body.appendChild(el("div", { class: "sc-summary-card__index", text: t(locale, "product_prefix") + product.index }));
      body.appendChild(el("div", { class: "sc-summary-card__title", text: product.title || "—" }));
      body.appendChild(el("div", { class: "sc-summary-card__brand", text: product.brand || "—" }));

      var facts = el("div", { class: "sc-summary-card__facts" });
      if (product.sku)   facts.appendChild(el("div", { class: "sc-summary-fact", text: "SKU: " + product.sku }));
      if (product.color) facts.appendChild(el("div", { class: "sc-summary-fact", text: "Color: " + product.color }));
      if (product.size)  facts.appendChild(el("div", { class: "sc-summary-fact", text: "Size: " + product.size }));
      facts.appendChild(el("div", { class: "sc-summary-fact", text: "Qty: " + (product.qty || 1) }));
      if (product.notes) facts.appendChild(el("div", { class: "sc-summary-fact", text: "Notes: " + product.notes }));
      body.appendChild(facts);

      if (product.snippet) body.appendChild(el("div", { class: "sc-summary-card__snippet", text: product.snippet }));
      if (product.official_link) body.appendChild(el("a", {
        class: "sc-summary-card__link", href: product.official_link,
        target: "_blank", rel: "noopener noreferrer", text: t(locale, "view_ref"),
      }));

      top.appendChild(body);
      card.appendChild(top);
      grid.appendChild(card);
    });
    panel.appendChild(grid);
    summaryWrap.appendChild(panel);
  }

  function clearSummary(w) { w.innerHTML = ""; w.style.display = "none"; }

  /* ==========================================================
     RENDER: product action card — simple: image + title + select
     The ENTIRE CARD is tappable (not just the small button).
     Uses touch tracking to distinguish tap from horizontal scroll.
     ========================================================== */
  function renderActionCard(wrap, action, locale, onClick) {
    var card = el("div", { class: "sc-product-card" });
    card.style.cursor = "pointer";

    // Gallery
    if (action.images && action.images.length) card.appendChild(renderGallery(action.images, "sc-product-card"));

    // Body: title + subtitle only (details are in the message above)
    var body = el("div", { class: "sc-product-card__body" });
    if (action.title)    body.appendChild(el("div", { class: "sc-product-card__title", text: action.title }));
    if (action.subtitle) body.appendChild(el("div", { class: "sc-product-card__subtitle", text: action.subtitle }));
    card.appendChild(body);

    // Footer: Select button label (visual only — the whole card is tappable)
    var footer = el("div", { class: "sc-product-card__footer" });
    var cls = action.variant === "finish"
      ? "sc-product-card__select sc-product-card__select--finish"
      : "sc-product-card__select";
    var btnLabel = localizeActionLabel(action.label, action.payload, locale);
    footer.appendChild(el("div", { class: cls, text: btnLabel }));
    card.appendChild(footer);

    // Make the ENTIRE CARD tappable — tap vs scroll detection
    var startX = 0;
    var startY = 0;
    var fired = false;
    card.addEventListener("touchstart", function (e) {
      var touch = e.touches[0];
      startX = touch.clientX;
      startY = touch.clientY;
      fired = false;
    }, { passive: true });
    card.addEventListener("touchend", function (e) {
      if (fired) return;
      var touch = e.changedTouches[0];
      var dx = Math.abs(touch.clientX - startX);
      var dy = Math.abs(touch.clientY - startY);
      // Only fire if finger moved less than 10px (a real tap, not a scroll)
      if (dx < 10 && dy < 10) {
        fired = true;
        e.preventDefault();
        tapFeedback(card);
        onClick(action);
      }
    });
    // Desktop fallback
    card.addEventListener("click", function (e) {
      if (fired) { fired = false; return; }
      e.preventDefault();
      tapFeedback(card);
      onClick(action);
    });

    wrap.appendChild(card);
  }

  /* ========== RENDER: simple action button (i18n) ========== */
  function renderSimpleAction(wrap, action, locale, onClick) {
    var cls = action.variant === "finish" ? "sc-action sc-action--finish" : "sc-action";
    var btnLabel = localizeActionLabel(action.label, action.payload, locale);
    var btn = el("button", { class: cls, type: "button", text: btnLabel });
    /* Optional two-line button: `sublabel` renders smaller under the label.
       ~text~ inside the sublabel becomes strikethrough (e.g. "instead of ~₩1,198,800~"). */
    if (action.sublabel) {
      btn.textContent = "";
      btn.classList.add("sc-action--two");
      btn.appendChild(el("span", { class: "sc-action__main", text: btnLabel }));
      var sub = el("span", { class: "sc-action__sub" });
      String(action.sublabel).split("~").forEach(function (part, i) {
        if (!part) return;
        if (i % 2 === 1) { var s = document.createElement("s"); s.textContent = part; sub.appendChild(s); }
        else sub.appendChild(document.createTextNode(part));
      });
      btn.appendChild(sub);
    }
    btn.addEventListener("click", function () { tapFeedback(btn); onClick(action); });
    wrap.appendChild(btn);
  }

  /* ========== Localize action label based on payload ========== */
  function localizeActionLabel(originalLabel, payload, locale) {
    if (!payload) return originalLabel || t(locale, "select");
    var p = String(payload);

    // Map known payloads to i18n keys
    if (p === "ACTION:SUBMIT")              return t(locale, "confirm_send");
    if (p === "ACTION:ADD_ANOTHER_PRODUCT") return t(locale, "add_another");
    if (p === "ACTION:FINISH_REQUEST")      return t(locale, "finish");
    if (p === "ACTION:NONE_OF_THESE")       return t(locale, "none_of_these");
    if (p === "ACTION:CONFIRM_EMAIL")       return t(locale, "yes");
    if (p === "ACTION:CHANGE_EMAIL")        return t(locale, "no_change");
    if (p === "ACTION:CONFIRM_LINK_PRODUCT") return t(locale, "yes");
    if (p === "ACTION:REJECT_LINK_PRODUCT") return t(locale, "no_change");
    if (p === "ACTION:SKIP_COLOR" || p === "ACTION:SKIP_SIZE" || p === "ACTION:SKIP_NOTES")
      return t(locale, "skip");
    if (p === "ACTION:EDIT_LAST_PRODUCT")   return t(locale, "edit_last");
    if (p === "ACTION:REMOVE_LAST_PRODUCT") return t(locale, "remove_last");

    // ACTION:SELECT:1, ACTION:SELECT:2, ACTION:SELECT:3 → use original label (product name)
    if (p.startsWith("ACTION:SELECT:"))     return originalLabel || t(locale, "select");

    // Market buttons → use original label (country name)
    if (p.startsWith("ACTION:MARKET:"))     return originalLabel || p;

    return originalLabel || t(locale, "select");
  }

  /* ========== RENDER: all actions (carousel on mobile when cards) ========== */
  function renderActions(wrap, actions, locale, onClick) {
    wrap.innerHTML = "";
    wrap.classList.remove("sc-actions--carousel");
    /* Hide "Check a price" in pro-order mode — only "Create an order" */
    var _root = wrap.closest("[data-solum-concierge]");
    if (_root && _root.getAttribute("data-mode") === "pro-order" && Array.isArray(actions)) {
      actions = actions.filter(function(a) { return !a || a.payload !== "ACTION:PRO_CHECK_PRICE"; });
    }
    /* Hide "Professional access" and "Private Sale" from standard chat */
    if (Array.isArray(actions)) {
      actions = actions.filter(function(a) { return !a || (a.payload !== "ACTION:WELCOME_PRO" && a.payload !== "ACTION:WELCOME_PRIVATE_SALE"); });
    }
    var has = Array.isArray(actions) && actions.length > 0;
    wrap.style.display = has ? "flex" : "none";
    if (!has) return;
    var hasCards = actions.some(function (a) { return a && a.card; });
    if (hasCards && isMobile()) wrap.classList.add("sc-actions--carousel");
    actions.forEach(function (a) {
      if (a && a.card) renderActionCard(wrap, a, locale, onClick);
      else renderSimpleAction(wrap, a, locale, onClick);
    });
  }

  /* ==========================================================
     Normalize free text → action codes
     ========================================================== */
  function normalizeToAction(message, stage) {
    var raw = String(message || "").trim();
    var m = raw.toLowerCase();
    if (stage === "ask_market") {
      if (m === "korea" || m === "south korea" || m === "kr" || m === "한국" || m === "เกาหลี" || m === "เกาหลีใต้") return "ACTION:MARKET:KR";
      if (m === "japan" || m === "jp" || m === "日本" || m === "ญี่ปุ่น") return "ACTION:MARKET:JP";
      if (m === "hong kong" || m === "hk" || m === "香港" || m === "ฮ่องกง") return "ACTION:MARKET:HK";
      if (m === "thailand" || m === "th" || m === "ไทย" || m === "ประเทศไทย") return "ACTION:MARKET:TH";
      if (m === "other") return "ACTION:MARKET:OTHER";
    }
    if (stage === "present_suggestions") {
      if (m === "1") return "ACTION:SELECT:1";
      if (m === "2") return "ACTION:SELECT:2";
      if (m === "3") return "ACTION:SELECT:3";
      if (m.includes("none")) return "ACTION:NONE_OF_THESE";
    }
    if (stage === "confirm_link_product") {
      if (m === "yes" || m.includes("that's the one") || m.includes("thats the one")) return "ACTION:CONFIRM_LINK_PRODUCT";
      if (m === "no" || m.includes("change")) return "ACTION:REJECT_LINK_PRODUCT";
    }
    if (stage === "confirm_email") {
      if (m === "yes" || m.includes("correct")) return "ACTION:CONFIRM_EMAIL";
      if (m.includes("change") || m === "no") return "ACTION:CHANGE_EMAIL";
    }
    if (stage === "ask_color") {
      if (m.includes("no color") || m.includes("no preference")) return "ACTION:SKIP_COLOR";
    }
    if (stage === "ask_size") {
      if (m.includes("no size") || m.includes("not needed") || m === "none") return "ACTION:SKIP_SIZE";
    }
    if (stage === "ask_notes") {
      if (m.includes("no note") || m.includes("no extra") || m === "none") return "ACTION:SKIP_NOTES";
    }
    if (stage === "after_product") {
      if (m.includes("another") || m.includes("add another")) return "ACTION:ADD_ANOTHER_PRODUCT";
      if (m.includes("review") || m.includes("finish") || m.includes("done")) return "ACTION:FINISH_REQUEST";
    }
    if (stage === "recap") {
      if (m === "confirm" || m === "send" || m === "submit" || m === "confirm and send" || m === "confirm & send") return "ACTION:SUBMIT";
      if (m.includes("another") || m.includes("add another")) return "ACTION:ADD_ANOTHER_PRODUCT";
      if (m.includes("edit")) return "ACTION:EDIT_LAST_PRODUCT";
      if (m.includes("remove") || m.includes("delete")) return "ACTION:REMOVE_LAST_PRODUCT";
    }
    return raw;
  }

  /* ==========================================================
     MESSAGE CLEANUP — when cards are shown, strip redundant
     numbered product listings from the assistant message.
     ========================================================== */
  function cleanSuggestionMessage(msg) {
    // Remove numbered lines like "1. Product Name – https://..."
    // and trailing instructions like "You can open them first..."
    var lines = msg.split("\n");
    var cleaned = [];
    for (var i = 0; i < lines.length; i++) {
      var line = lines[i].trim();
      // Skip numbered product lines: "1.", "2.", "3." followed by URL or product text
      if (/^\d+[\.\)]\s/.test(line)) continue;
      // Skip "You can open them first..." or similar instruction lines
      if (/open them|choose the closest|look at (them|each)/i.test(line)) continue;
      // Skip lines that are just URLs
      if (/^https?:\/\//.test(line)) continue;
      // Skip empty lines at the end
      if (line) cleaned.push(lines[i]);
    }
    var result = cleaned.join("\n").trim();
    // If we stripped everything, return a minimal intro
    return result || msg.split("\n")[0] || msg;
  }

  /* ==========================================================
     INJECT MISSING QUESTION — when server sends a transition
     message without the actual question (edit flow), add it.
     ========================================================== */
  var STAGE_QUESTIONS = {
    en: {
      ask_color: "Which colour would you like for this item?",
      ask_size:  "What size do you need?",
      ask_notes: "Any special notes or requests?",
      ask_brand: "Which brand are you looking for?",
      ask_product_input: "What product are you looking for?",
    },
    ko: {
      ask_color: "이 상품의 원하시는 색상은 무엇인가요?",
      ask_size:  "어떤 사이즈가 필요하신가요?",
      ask_notes: "추가 요청이나 메모가 있으신가요?",
      ask_brand: "어떤 브랜드를 찾고 계신가요?",
      ask_product_input: "어떤 제품을 찾고 계신가요?",
    },
    ja: {
      ask_color: "このアイテムのご希望の色は何ですか？",
      ask_size:  "サイズはいかがなさいますか？",
      ask_notes: "特別なご要望やメモはありますか？",
      ask_brand: "どのブランドをお探しですか？",
      ask_product_input: "どの商品をお探しですか？",
    },
    "zh-hk": {
      ask_color: "你想要什麼顏色？",
      ask_size:  "你需要什麼尺寸？",
      ask_notes: "有任何特別備註嗎？",
      ask_brand: "你在找什麼品牌？",
      ask_product_input: "你在找什麼產品？",
    },
    th: {
      ask_color: "คุณต้องการสีอะไรสำหรับสินค้านี้?",
      ask_size:  "คุณต้องการไซส์อะไร?",
      ask_notes: "มีหมายเหตุพิเศษหรือคำขอเพิ่มเติมไหม?",
      ask_brand: "คุณกำลังมองหาแบรนด์อะไร?",
      ask_product_input: "คุณกำลังมองหาสินค้าอะไร?",
    },
  };

  function injectMissingQuestion(msg, stage, locale) {
    var questions = STAGE_QUESTIONS[locale] || STAGE_QUESTIONS.en;
    var expectedQ = questions[stage];
    if (!expectedQ) return msg;

    // Check if the message already contains a question about the topic
    var m = msg.toLowerCase();
    var hasQuestion = false;
    if (stage === "ask_color")         hasQuestion = /colo[u]?r|색상|色|カラー|顏色|สี/i.test(m);
    else if (stage === "ask_size")     hasQuestion = /size|사이즈|サイズ|尺寸|ไซส์/i.test(m);
    else if (stage === "ask_notes")    hasQuestion = /note|request|메모|요청|メモ|備註|หมายเหตุ/i.test(m);
    else if (stage === "ask_brand")    hasQuestion = /brand|브랜드|ブランド|品牌|แบรนด์/i.test(m);
    else if (stage === "ask_product_input") hasQuestion = /product|looking for|제품|商品|產品|สินค้า/i.test(m);

    if (hasQuestion) return msg;

    // Append the missing question
    return msg + "\n\n" + expectedQ;
  }

  /* ==========================================================
     Core send
     ========================================================== */
  async function sendMessage(root, fsManager, locale, message, displayText) {
    var list       = $(".sc-list", root);
    var actionsW   = $(".sc-actions", root);
    var summaryW   = $(".sc-summary", root);
    var session_id = getSessionId();
    var curStage   = getStage(root);
    var normalized = normalizeToAction(message, curStage);

    /* ── Demo mode: intercept submit → show login card ── */
    if (root.getAttribute("data-mode") === "demo" && normalized === "ACTION:SUBMIT") {
      var _isEn = (root.getAttribute("data-locale") || "").indexOf("ko") === -1;
      var _prefix = window.location.pathname.indexOf("/en") === 0 ? "/en" : "";
      var _retUrl = encodeURIComponent(window.location.pathname + "#concierge-chat");
      var _loginUrl = _prefix + "/customer_authentication/login?return_to=" + _retUrl;
      var _registerUrl = _prefix + "/account/register";
      var _list = $(".sc-list", root);
      /* Render a styled card directly in the DOM */
      var _card = document.createElement("div");
      _card.style.cssText = "align-self:flex-start;max-width:88%;background:#0a0a0a;border-radius:16px;padding:24px 22px;margin:8px 0;";
      _card.innerHTML = "<div style=\"font-family:Libre Baskerville,serif;font-size:16px;font-weight:700;color:#f5f5f5;margin-bottom:8px\">"
        + (_isEn ? "One last step" : "마지막 단계")
        + "</div><div style=\"font-family:Noto Sans KR,sans-serif;font-size:13px;color:rgba(255,255,255,.7);line-height:1.6;margin-bottom:20px\">"
        + (_isEn ? "Create a free account to send your request. It only takes a moment." : "요청을 보내려면 무료 계정을 만드세요. 잠깐이면 됩니다.")
        + "</div><a href=\"" + _registerUrl + "\" style=\"display:block;text-align:center;padding:14px;border-radius:8px;background:#f5f5f5;color:#0a0a0a;font-family:Raleway,sans-serif;font-size:12px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;text-decoration:none;margin-bottom:10px\">"
        + (_isEn ? "Create Account" : "회원가입")
        + "</a><a href=\"" + _loginUrl + "\" style=\"display:block;text-align:center;padding:12px;font-family:Raleway,sans-serif;font-size:12px;color:rgba(255,255,255,.6);text-decoration:none\">"
        + (_isEn ? "Already have an account? Log in" : "이미 계정이 있으신가요? 로그인")
        + "</a>";
      _list.appendChild(_card);
      _list.scrollTop = _list.scrollHeight;
      return;
    }

    if (fsManager && isMobile()) fsManager.enter();

    renderMessage(list, "user", displayText || message);
    renderActions(actionsW, [], locale, function () {});
    var typingNode = showTyping(list);

    try {
      var payload = {
        session_id: session_id,
        locale: locale,
        message: normalized,
      };
      // Pass Shopify customer info if available (logged-in user)
      var custName = root.getAttribute("data-customer-name");
      var custEmail = root.getAttribute("data-customer-email");
      var custTags = root.getAttribute("data-customer-tags");
      var chatMode = root.getAttribute("data-mode");
      // Fallback: read chat_mode from URL params (?chat_mode=pro-signup&market=kr)
      if (!chatMode) {
        try {
          var _qp = new URLSearchParams(window.location.search);
          var _m = _qp.get("chat_mode");
          if (_m) chatMode = _m;
        } catch (e) {}
      }
      var marketAttr = root.getAttribute("data-market");
      if (!marketAttr) {
        try {
          var _qp2 = new URLSearchParams(window.location.search);
          var _mk = _qp2.get("market");
          if (_mk) marketAttr = _mk;
        } catch (e) {}
      }
      if (custName) payload.customer_name = custName;
      if (custEmail) payload.customer_email = custEmail;
      if (custTags) payload.customer_tags = custTags;
      if (chatMode) payload.chat_mode = chatMode;
      if (marketAttr) payload.market = marketAttr;

      var data = await postJSON(API_BASE + "/api/concierge/message", payload);

      // Store cart_attributes for pro-signup checkout (fired on recap stage)
      if (data && data.cart_attributes && typeof data.cart_attributes === "object") {
        window.__solumProSignupAttrs = data.cart_attributes;
      }

      removeTyping(typingNode);

      if (data && data.state && data.state.stage) {
        setStage(root, data.state.stage);
        updateProgressBar(root, data.state.stage, locale);
        updatePlaceholder(root, data.state.stage, locale);
        // Apply input formatting for KR phone / PCCC stages
        var mkt = (data.state && data.state.pro_registration && data.state.pro_registration.market) || (data.state && data.state.contact && data.state.contact.market) || contextData.market || "";
        setupInputFormatting(root, data.state.stage, mkt);
      }

      // Update floating context badge with latest user info
      extractContext(data);
      updateContextBadge(root);

      var actions = Array.isArray(data.actions) ? data.actions : [];
      var assistMsg = data.assistant_message || "";
      var hasCards = actions.some(function (a) { return a && a.card; });
      var stage = (data.state && data.state.stage) || "";

      // ── HIGH-DEMAND BRAND WARNING ──
      // When the user just typed / selected a brand at a brand-input stage,
      // check the actual message they sent (more reliable than API state).
      // Also fall back to data.state.brand / contextData.brand just in case.
      var isBrandStage = (
          curStage === "ask_brand" ||
          curStage === "pro_order_brand" ||
          curStage === "pro_order_brand_manual" ||
          curStage === "pro_check_brand"
      );
      if (isBrandStage) {
        var brandForWarning = message || (data.state && data.state.brand) || contextData.brand || "";
        var warningText = getBrandWarning(brandForWarning, locale);
        if (warningText) {
          renderBrandWarning(list, warningText);
        }
      }

      // Keep the full assistant message with product info + links.
      // The user reads details + official links in the message,
      // then uses the carousel cards below to select a product.

      // When editing a product, the server sends a transition message
      // ("OK, let's update…") but may skip the actual question.
      // Inject the missing question so the user knows what to type.
      assistMsg = injectMissingQuestion(assistMsg, stage, locale);

      // Pro order submitted → show confirmation card with animation
      if (data.pro_confirmation && data.pro_confirmation.order_id) {
        renderConfirmationCard(list, data.pro_confirmation.order_id, locale);
        // Relais recherche dashboard : la modal chat se ferme toute seule
        // après la confirmation (demande Marc 2026-08-13)
        if (data.sa_autoclose && document.body.classList.contains("pd-chat-modal-open")) {
          setTimeout(function () { document.body.classList.remove("pd-chat-modal-open"); }, 6500);
        }
        if (assistMsg) renderMessage(list, "assistant", assistMsg);
      // Promo code validated → show green badge, then auto-advance to video step
      } else if (data.promo_validated && assistMsg) {
        renderPromoBadge(list, assistMsg);
      } else if (data.price_badge && data.price_badge.price) {
        // Pro price check result → render as styled badge card
        renderPriceBadge(list, data.price_badge);
      } else if (assistMsg) {
        renderMessage(list, "assistant", assistMsg, data.image_url ? { image_url: data.image_url } : undefined);
      }

      // Render inline video preview if present (video option step)
      if (data.video_preview_url) {
        renderVideoPreview(list, data.video_preview_url);
      }

      // After promo validated, auto-advance to video option step
      if (data.promo_validated && data.next_stage === "ask_video_option") {
        (async function() {
          try {
            var typV = showTyping(list);
            var vRes = await postJSON(API_BASE + "/api/concierge/message", { session_id: session_id, locale: locale, message: "ACTION:CONTINUE" });
            removeTyping(typV);
            if (vRes.assistant_message) renderMessage(list, "assistant", vRes.assistant_message);
            if (vRes.video_preview_url) renderVideoPreview(list, vRes.video_preview_url);
            var vActions = Array.isArray(vRes.actions) ? vRes.actions : [];
            renderActions(actionsW, vActions, locale, async function(vAction) {
              renderActions(actionsW, [], locale, function() {});
              renderMessage(list, "user", vAction.label || vAction.payload);
              var typV2 = showTyping(list);
              try {
                var vRes2 = await postJSON(API_BASE + "/api/concierge/message", { session_id: session_id, locale: locale, message: vAction.payload || vAction.label });
                removeTyping(typV2);
                if (vRes2.assistant_message) renderMessage(list, "assistant", vRes2.assistant_message);
                if (vRes2.auto_submit) {
                  var typSub = showTyping(list);
                  var subRes = await postJSON(API_BASE + "/api/concierge/submit", { session_id: session_id });
                  removeTyping(typSub);
                  clearSummary(summaryW);
                  setStage(root, "submitted");
                  updateProgressBar(root, "submitted", locale);
                  updatePlaceholder(root, "submitted", locale);
                  renderConfirmationCard(list, subRes.request_id, locale);
                  if (subRes.assistant_message) renderMessage(list, "assistant", subRes.assistant_message);
                }
              } catch(e2) {
                removeTyping(typV2);
                renderMessage(list, "assistant", e2.message || "Error.");
              }
            });
          } catch(e) {
            renderMessage(list, "assistant", e.message || "Error.");
          }
        })();
        return;
      }

      // Platform logos (visual marketplace indicators for KR order number step)
      if (data.platform_logos && data.platform_logos.length) {
        renderPlatformLogos(list, data.platform_logos);
      }

      // Product cards for order recap (with images)
      if (data.product_cards && data.product_cards.length) {
        renderProductCards(list, data.product_cards);
      }

      if (data.summary) renderSummary(summaryW, data.summary, locale);
      else clearSummary(summaryW);

      // auto_submit: after video/promo step, automatically submit the request
      if (data.auto_submit) {
        (async function () {
          try {
            var typSub = showTyping(list);
            var subRes = await postJSON(API_BASE + "/api/concierge/submit", { session_id: session_id });
            removeTyping(typSub);
            clearSummary(summaryW);
            setStage(root, "submitted");
            updateProgressBar(root, "submitted", locale);
            updatePlaceholder(root, "submitted", locale);
            renderConfirmationCard(list, subRes.request_id, locale);
            if (subRes.assistant_message) renderMessage(list, "assistant", subRes.assistant_message);
          } catch (e) {
            renderMessage(list, "assistant", e.message || "Error.");
          }
        })();
        return;
      }

      renderActions(actionsW, actions, locale, async function (action) {
        // Handle NEW pro signup checkout: clear cart, add variant + setup fee,
        // apply cart attributes (so they land in order.note_attributes for the
        // /webhooks/orders-paid handler), then redirect to checkout with email prefilled.
        if (action.payload && action.payload.indexOf("ACTION:PRO_SIGNUP_CHECKOUT:") === 0) {
          var sParts = action.payload.replace("ACTION:PRO_SIGNUP_CHECKOUT:", "").split(":");
          var sVariantId = parseInt(sParts[0], 10);
          var sSellingPlanId = parseInt(sParts[1], 10);
          var sSetupVariantId = sParts[2] ? parseInt(sParts[2], 10) : 0;
          renderActions(actionsW, [], locale, function () {});
          var sLoadingMsg = locale === "ko" ? "결제 페이지를 준비 중입니다..."
                        : locale === "ja" ? "お支払いページを準備しています..."
                        : locale === "zh-hk" ? "正在準備付款頁面..."
                        : locale === "th" ? "กำลังเตรียมหน้าชำระเงิน..."
                        : "Preparing secure checkout...";
          renderMessage(list, "assistant", sLoadingMsg);
          scrollToBottom(list);
          try {
            await fetch("/cart/clear.js", { method: "POST" });
            // 1. Subscription variant + selling plan (required)
            // PRO (2026-09-16) : sVariantId 0 = pas d'abonnement, seul le setup fee part au panier
            var sSubResp = await fetch("/cart/add.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(sVariantId ? { id: sVariantId, quantity: 1, selling_plan: sSellingPlanId } : { id: sSetupVariantId, quantity: 1 })
            });
            if (!sSubResp.ok) {
              var sErr = "";
              try { sErr = await sSubResp.text(); } catch (e) {}
              console.error("[SC-CHAT] pro-signup subscription add failed:", sSubResp.status, sErr);
              throw new Error("Subscription add failed (" + sSubResp.status + ")");
            }
            // 2. One-time setup fee (non-blocking) — déjà ajouté en 1 si pas d'abonnement
            if (sSetupVariantId && sVariantId) {
              try {
                var sSetupResp = await fetch("/cart/add.js", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ id: sSetupVariantId, quantity: 1 })
                });
                if (!sSetupResp.ok) {
                  var sSetupErr = "";
                  try { sSetupErr = await sSetupResp.text(); } catch (e) {}
                  console.error("[SC-CHAT] pro-signup setup fee add failed:", sSetupResp.status, sSetupErr);
                }
              } catch (sSetupExc) {
                console.error("[SC-CHAT] pro-signup setup fee add exception:", sSetupExc);
              }
            }
            // 3. Apply cart attributes (will land as order.note_attributes)
            var sAttrs = window.__solumProSignupAttrs || {};
            try {
              await fetch("/cart/update.js", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ attributes: sAttrs })
              });
            } catch (sAttrErr) {
              console.error("[SC-CHAT] pro-signup cart attributes update failed:", sAttrErr);
            }
            // 4. Redirect to checkout with email pre-filled for reduced friction
            var sCheckoutUrl = "/checkout";
            if (sAttrs && sAttrs.signup_email) {
              sCheckoutUrl = "/checkout?checkout%5Bemail%5D=" + encodeURIComponent(sAttrs.signup_email);
            }
            window.location.href = sCheckoutUrl;
          } catch (sExc) {
            var sErrMsg = locale === "ko" ? "결제 페이지를 열 수 없습니다. 다시 시도해 주세요."
                       : locale === "ja" ? "お支払いページを開けませんでした。もう一度お試しください。"
                       : locale === "zh-hk" ? "無法打開付款頁面，請重試。"
                       : locale === "th" ? "ไม่สามารถเปิดหน้าชำระเงินได้ กรุณาลองอีกครั้ง"
                       : "Could not open checkout. Please try again.";
            renderMessage(list, "assistant", sErrMsg);
            scrollToBottom(list);
          }
          return;
        }

        // Handle subscription payment: AJAX cart add + redirect to checkout
        if (action.payload && action.payload.indexOf("ACTION:PRO_SUBSCRIBE:") === 0) {
          var parts = action.payload.replace("ACTION:PRO_SUBSCRIBE:", "").split(":");
          var variantId = parseInt(parts[0], 10);
          var sellingPlanId = parseInt(parts[1], 10);
          var setupVariantId = parts[2] ? parseInt(parts[2], 10) : 0;
          renderActions(actionsW, [], locale, function () {});
          var loadingMsg = locale === "ko" ? "결제 페이지를 준비 중입니다..."
                        : locale === "ja" ? "お支払いページを準備しています..."
                        : locale === "zh-hk" ? "正在準備付款頁面..."
                        : locale === "th" ? "กำลังเตรียมหน้าชำระเงิน..."
                        : "Preparing checkout...";
          renderMessage(list, "assistant", loadingMsg);
          scrollToBottom(list);
          try {
            // Clear cart, add subscription item, then redirect to checkout
            await fetch("/cart/clear.js", { method: "POST" });
            // Add subscription first (required)
            // PRO (2026-09-16) : variantId 0 = pas d'abonnement, seul le setup fee part au panier
            var subResp = await fetch("/cart/add.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(variantId ? { id: variantId, quantity: 1, selling_plan: sellingPlanId } : { id: setupVariantId, quantity: 1 })
            });
            if (!subResp.ok) {
              var subErrTxt = "";
              try { subErrTxt = await subResp.text(); } catch (e) {}
              console.error("[SC-CHAT] subscription add failed:", subResp.status, subErrTxt);
              throw new Error("Subscription add failed (" + subResp.status + ")");
            }
            // Then add the one-time setup fee (non-blocking if it fails, still checkout) — déjà ajouté si pas d'abonnement
            if (setupVariantId && variantId) {
              try {
                var setupResp = await fetch("/cart/add.js", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ id: setupVariantId, quantity: 1 })
                });
                if (!setupResp.ok) {
                  var setupErrTxt = "";
                  try { setupErrTxt = await setupResp.text(); } catch (e) {}
                  console.error("[SC-CHAT] setup fee add failed:", setupResp.status, setupErrTxt);
                }
              } catch (setupErr) {
                console.error("[SC-CHAT] setup fee add exception:", setupErr);
              }
            }
            // Attributs panier (tier/market/email) → note_attributes de la commande (webhook orders-paid)
            var subAttrs = window.__solumProSignupAttrs || null;
            if (subAttrs) {
              try {
                await fetch("/cart/update.js", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ attributes: subAttrs }) });
              } catch (subAttrErr) { console.error("[SC-CHAT] cart attributes update failed:", subAttrErr); }
            }
            window.location.href = "/checkout";
          } catch (err) {
            var errMsg = locale === "ko" ? "결제 페이지를 열 수 없습니다. 다시 시도해 주세요."
                       : locale === "ja" ? "お支払いページを開けませんでした。もう一度お試しください。"
                       : locale === "zh-hk" ? "無法打開付款頁面，請重試。"
                       : locale === "th" ? "ไม่สามารถเปิดหน้าชำระเงินได้ กรุณาลองอีกครั้ง"
                       : "Could not open checkout. Please try again.";
            renderMessage(list, "assistant", errMsg);
            scrollToBottom(list);
          }
          return;
        }

        // Handle Private Sale add-to-cart: AJAX cart add + redirect to checkout
        if (action.payload && action.payload.indexOf("ACTION:PS_ADD_CART:") === 0) {
          var psVariantId = parseInt(action.payload.replace("ACTION:PS_ADD_CART:", ""), 10);
          renderActions(actionsW, [], locale, function () {});
          var psLoadMsg = locale === "ko" ? "장바구니에 추가하고 결제 페이지를 준비 중입니다..."
                        : locale === "ja" ? "カートに追加し、お支払いページを準備しています..."
                        : locale === "zh-hk" ? "正在加入購物車並準備付款頁面..."
                        : locale === "th" ? "กำลังเพิ่มลงตะกร้าและเตรียมหน้าชำระเงิน..."
                        : "Adding to cart and preparing checkout...";
          renderMessage(list, "assistant", psLoadMsg);
          scrollToBottom(list);
          try {
            await fetch("/cart/add.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ items: [{ id: psVariantId, quantity: 1 }] })
            });
            window.location.href = "/cart";
          } catch (psErr) {
            var psErrMsg = locale === "ko" ? "장바구니에 추가할 수 없습니다. 다시 시도해 주세요."
                         : locale === "ja" ? "カートに追加できませんでした。もう一度お試しください。"
                         : locale === "zh-hk" ? "無法加入購物車，請重試。"
                         : locale === "th" ? "ไม่สามารถเพิ่มลงตะกร้าได้ กรุณาลองอีกครั้ง"
                         : "Could not add to cart. Please try again.";
            renderMessage(list, "assistant", psErrMsg);
            scrollToBottom(list);
          }
          return;
        }

        renderActions(actionsW, [], locale, function () {});
        try {
          var displayLabel = localizeActionLabel(action.label, action.payload, locale);
          await sendMessage(root, fsManager, locale, action.payload, displayLabel);
        } catch (e) {
          renderMessage(list, "assistant", e.message || "Error.");
        }
      });

      // ── IMAGE DISCLAIMER below product suggestion cards ──
      // Rendered inside the chat list (not actionsW) so it never
      // breaks the carousel / card flex layout.
      // Show whenever there are product cards with images, regardless of stage.
      if (hasCards) {
        renderImageDisclaimer(list, locale);
      }

      // If action buttons are present, blur the input so the keyboard
      // closes cleanly — the user will tap a button, not type.
      var inputEl = $(".sc-input", root);
      if (actions.length > 0 && inputEl) {
        inputEl.blur();
      }
      // Scroll to bottom after a short delay to let viewport settle
      setTimeout(function () {
        scrollToBottom(list);
      }, actions.length > 0 ? 400 : 100);
    } catch (e) {
      removeTyping(typingNode);
      renderMessage(list, "assistant", e.message || "Error.");
    }
  }

  /* ==========================================================
     Init
     ========================================================== */
  function initOne(root) {
    if (root._scInitDone) return;
    root._scInitDone = true;
    var section  = root.closest(".sc-concierge");
    var list     = $(".sc-list", root);
    var input    = $(".sc-input", root);
    var sendBtn  = $(".sc-send", root);
    var actionsW = $(".sc-actions", root);
    var summaryW = $(".sc-summary", root);
    var resetBtn = $(".sc-reset", root);

    resetSessionId();
    setStage(root, "ask_first_name");

    if (actionsW) actionsW.style.display = "none";
    if (summaryW) summaryW.style.display = "none";

    var locale   = normalizeLocale(root.getAttribute("data-locale") || "en");
    var greeting = (root.getAttribute("data-greeting") || defaultGreeting(locale)).replace(/\\n/g, "\n");

    preventIOSZoom();

    var fsManager = section ? createFullscreenManager(section, root) : null;
    if (section) createKeyboardManager(section, root);

    // Brand autocomplete
    var brandAC = createBrandAutocomplete(root);

    // Check if Shopify customer is logged in
    var customerName = root.getAttribute("data-customer-name") || root.getAttribute("data-customer-email");
    var customerEmail = root.getAttribute("data-customer-email");
    var isLoggedIn = !!(customerName && customerEmail);

    if (isLoggedIn) {
      // Logged-in user: skip greeting, go straight to "Welcome back"
      setStage(root, "welcome");
      updateProgressBar(root, "welcome", locale);
      updatePlaceholder(root, "welcome", locale);
      // Auto-send the name silently (no user bubble shown)
      (async function() {
        var typingNode = showTyping(list);
        try {
          var initPayload = {
            session_id: getSessionId(),
            locale: locale,
            message: customerName,
            customer_name: customerName,
            customer_email: customerEmail,
          };
          var chatModeAttr = root.getAttribute("data-mode");
          if (chatModeAttr) initPayload.chat_mode = chatModeAttr;
          var data = await postJSON(API_BASE + "/api/concierge/message", initPayload);
          removeTyping(typingNode);
          if (data && data.state && data.state.stage) {
            setStage(root, data.state.stage);
            updateProgressBar(root, data.state.stage, locale);
            updatePlaceholder(root, data.state.stage, locale);
          }
          extractContext(data);
          updateContextBadge(root);
          var actions = Array.isArray(data.actions) ? data.actions : [];
          var assistMsg = data.assistant_message || "";
          if (assistMsg) renderMessage(list, "assistant", assistMsg);
          renderActions(actionsW, actions, locale, async function(action) {
            if (action.payload && action.payload.indexOf("ACTION:PRO_SUBSCRIBE:") === 0) {
              // handled elsewhere
              return;
            }
            if (action.payload && action.payload.indexOf("ACTION:PS_ADD_CART:") === 0) {
              return;
            }
            renderActions(actionsW, [], locale, function () {});
            try {
              var displayLabel = localizeActionLabel(action.label, action.payload, locale);
              await sendMessage(root, fsManager, locale, action.payload, displayLabel);
            } catch (e) {
              renderMessage(list, "assistant", e.message || "Error.");
            }
          });
          scrollToBottom(list);
        } catch(e) {
          removeTyping(typingNode);
          renderMessage(list, "assistant", greeting);
        }
      })();
    } else {
      // Not logged in: check if we're in pro-signup mode — if so, auto-kick the flow
      // so the user lands directly on "What is your business name?" without first
      // being asked for their personal name (handled later in pro_signup_contact).
      var _psChatMode = root.getAttribute("data-mode");
      if (!_psChatMode) {
        try { var _qpPs = new URLSearchParams(window.location.search); var _mPs = _qpPs.get("chat_mode"); if (_mPs) _psChatMode = _mPs; } catch (e) {}
      }
      var _psMarket = root.getAttribute("data-market");
      if (!_psMarket) {
        try { var _qpPs2 = new URLSearchParams(window.location.search); var _mkPs = _qpPs2.get("market"); if (_mkPs) _psMarket = _mkPs; } catch (e) {}
      }
      if (_psChatMode === "pro-signup" || _psChatMode === "ultimate-signup") {
        // Auto-init pro-signup flow (no login required)
        // NOTE: we intentionally skip the default greeting here — the backend
        // returns the real pro-signup first question ("What is your business name?")
        setStage(root, "welcome");
        updateProgressBar(root, "welcome", locale);
        updatePlaceholder(root, "welcome", locale);
        (async function () {
          var psTyping = showTyping(list);
          try {
            var psInitPayload = {
              session_id: getSessionId(),
              locale: locale,
              message: "start",
              chat_mode: _psChatMode,
            };
            if (_psMarket) psInitPayload.market = _psMarket;
            var psData = await postJSON(API_BASE + "/api/concierge/message", psInitPayload);
            removeTyping(psTyping);
            if (psData && psData.cart_attributes && typeof psData.cart_attributes === "object") {
              window.__solumProSignupAttrs = psData.cart_attributes;
            }
            if (psData && psData.state && psData.state.stage) {
              setStage(root, psData.state.stage);
              updateProgressBar(root, psData.state.stage, locale);
              updatePlaceholder(root, psData.state.stage, locale);
            }
            var psActions = Array.isArray(psData.actions) ? psData.actions : [];
            var psMsg = psData.assistant_message || "";
            if (psMsg) renderMessage(list, "assistant", psMsg);
            renderActions(actionsW, psActions, locale, async function (action) {
              if (action.payload && action.payload.indexOf("ACTION:PRO_SUBSCRIBE:") === 0) return;
              if (action.payload && action.payload.indexOf("ACTION:PRO_SIGNUP_CHECKOUT:") === 0) return;
              if (action.payload && action.payload.indexOf("ACTION:PS_ADD_CART:") === 0) return;
              renderActions(actionsW, [], locale, function () {});
              try {
                var displayLabel = localizeActionLabel(action.label, action.payload, locale);
                await sendMessage(root, fsManager, locale, action.payload, displayLabel);
              } catch (e) {
                renderMessage(list, "assistant", e.message || "Error.");
              }
            });
            scrollToBottom(list);
          } catch (psErr) {
            removeTyping(psTyping);
            renderMessage(list, "assistant", greeting);
          }
        })();
      } else {
        // Normal non-logged-in flow — ask for name
        // Defer greeting so SolumStartPro can cancel it if pro-signup is triggered
        updateProgressBar(root, "ask_first_name", locale);
        updatePlaceholder(root, "ask_first_name", locale);
        root._greetingTimer = setTimeout(function(){
          var m = root.getAttribute("data-mode");
          if (m === "pro-signup" || m === "ultimate-signup") return;
          if (!root.querySelector(".sc-list .sc-msg")) {
            renderMessage(list, "assistant", greeting);
          }
        }, 150);
      }
    }

    sendBtn.addEventListener("click", async function () {
      var msg = input.value.trim();
      if (!msg) return;
      input.value = "";
      tapFeedback(sendBtn);
      // Keep input focused so keyboard stays open between questions
      // (prevents keyboard close→reopen animation glitch on iOS)
      if (isMobile()) {
        requestAnimationFrame(function () { input.focus(); });
      }
      await sendMessage(root, fsManager, locale, msg, msg);
    });

    // Envoi d'une ACTION machine depuis l'extérieur (recherche dashboard) :
    // payload = ACTION:… , label = la bulle affichée côté Pro. Le flux
    // d'origine (boutons, liens collés) n'est pas touché.
    root._scSendAction = function (payload, label) {
      return sendMessage(root, fsManager, locale, payload, label || payload);
    };

    input.addEventListener("keydown", function (ev) {
      // Don't send if autocomplete is handling the Enter key
      if (ev.key === "Enter" && !brandAC.isOpen()) { ev.preventDefault(); sendBtn.click(); }
    });

    if (resetBtn) {
      resetBtn.addEventListener("click", async function () {
        tapFeedback(resetBtn);
        resetSessionId();
        list.innerHTML = "";
        actionsW.innerHTML = "";
        actionsW.style.display = "none";
        actionsW.classList.remove("sc-actions--carousel");
        clearSummary(summaryW);
        // Reset context badge
        contextData.name = "";
        contextData.market = "";
        contextData.brand = "";
        updateContextBadge(root);

        if (isLoggedIn) {
          // Logged-in user: replay the welcome flow (auto-send → "Welcome back" from backend).
          // Do NOT render the static "What's your name?" greeting — backend personalizes it.
          setStage(root, "welcome");
          updateProgressBar(root, "welcome", locale);
          updatePlaceholder(root, "welcome", locale);
          var typingNode = showTyping(list);
          try {
            var initPayload = {
              session_id: getSessionId(),
              locale: locale,
              message: customerName,
              customer_name: customerName,
              customer_email: customerEmail,
            };
            var chatModeAttr = root.getAttribute("data-mode");
            if (!chatModeAttr) {
              try { var _qpInit = new URLSearchParams(window.location.search); var _mInit = _qpInit.get("chat_mode"); if (_mInit) chatModeAttr = _mInit; } catch (e) {}
            }
            var marketAttrInit = root.getAttribute("data-market");
            if (!marketAttrInit) {
              try { var _qpInit2 = new URLSearchParams(window.location.search); var _mkInit = _qpInit2.get("market"); if (_mkInit) marketAttrInit = _mkInit; } catch (e) {}
            }
            if (chatModeAttr) initPayload.chat_mode = chatModeAttr;
            if (marketAttrInit) initPayload.market = marketAttrInit;
            var data = await postJSON(API_BASE + "/api/concierge/message", initPayload);
            removeTyping(typingNode);
            if (data && data.state && data.state.stage) {
              setStage(root, data.state.stage);
              updateProgressBar(root, data.state.stage, locale);
              updatePlaceholder(root, data.state.stage, locale);
            }
            extractContext(data);
            updateContextBadge(root);
            var actions = Array.isArray(data.actions) ? data.actions : [];
            var assistMsg = data.assistant_message || "";
            if (assistMsg) renderMessage(list, "assistant", assistMsg);
            renderActions(actionsW, actions, locale, async function(action) {
              if (action.payload && action.payload.indexOf("ACTION:PRO_SUBSCRIBE:") === 0) return;
              if (action.payload && action.payload.indexOf("ACTION:PS_ADD_CART:") === 0) return;
              renderActions(actionsW, [], locale, function () {});
              try {
                var displayLabel = localizeActionLabel(action.label, action.payload, locale);
                await sendMessage(root, fsManager, locale, action.payload, displayLabel);
              } catch (e) {
                renderMessage(list, "assistant", e.message || "Error.");
              }
            });
            scrollToBottom(list);
          } catch(e) {
            removeTyping(typingNode);
            renderMessage(list, "assistant", greeting);
          }
        } else {
          // Not logged in: check if a pro-signup mode is now set → auto-kick pro flow
          var _rsMode = root.getAttribute("data-mode");
          var _rsMarket = root.getAttribute("data-market") || "kr";
          if (_rsMode === "pro-signup" || _rsMode === "ultimate-signup") {
            setStage(root, "welcome");
            updateProgressBar(root, "welcome", locale);
            updatePlaceholder(root, "welcome", locale);
            (async function () {
              var rsTyping = showTyping(list);
              try {
                var rsPayload = {
                  session_id: getSessionId(),
                  locale: locale,
                  message: "start",
                  chat_mode: _rsMode,
                  market: _rsMarket,
                };
                var rsData = await postJSON(API_BASE + "/api/concierge/message", rsPayload);
                removeTyping(rsTyping);
                if (rsData && rsData.cart_attributes && typeof rsData.cart_attributes === "object") {
                  window.__solumProSignupAttrs = rsData.cart_attributes;
                }
                if (rsData && rsData.state && rsData.state.stage) {
                  setStage(root, rsData.state.stage);
                  updateProgressBar(root, rsData.state.stage, locale);
                  updatePlaceholder(root, rsData.state.stage, locale);
                }
                var rsActions = Array.isArray(rsData.actions) ? rsData.actions : [];
                var rsMsg = rsData.assistant_message || "";
                if (rsMsg) renderMessage(list, "assistant", rsMsg);
                renderActions(actionsW, rsActions, locale, async function (action) {
                  if (action.payload && action.payload.indexOf("ACTION:PRO_SUBSCRIBE:") === 0) return;
                  if (action.payload && action.payload.indexOf("ACTION:PRO_SIGNUP_CHECKOUT:") === 0) return;
                  if (action.payload && action.payload.indexOf("ACTION:PS_ADD_CART:") === 0) return;
                  renderActions(actionsW, [], locale, function () {});
                  try {
                    var displayLabel = localizeActionLabel(action.label, action.payload, locale);
                    await sendMessage(root, fsManager, locale, action.payload, displayLabel);
                  } catch (e) {
                    renderMessage(list, "assistant", e.message || "Error.");
                  }
                });
                scrollToBottom(list);
              } catch (rsErr) {
                removeTyping(rsTyping);
                renderMessage(list, "assistant", greeting);
              }
            })();
          } else {
            // Normal reset — ask for name
            setStage(root, "ask_first_name");
            renderMessage(list, "assistant", greeting);
            updateProgressBar(root, "ask_first_name", locale);
            updatePlaceholder(root, "ask_first_name", locale);
          }
        }
      });
    }
  }

  // ── Public API: start a pro-signup flow from outside the widget ──
  window.SolumChatInit = function(el){ el._scInitDone = false; initOne(el); };
  /* Prefill the chat input from outside the widget (dashboard unified search:
     "Add to my list" hands over to the chat for size / color / recipient).
     autosend=true also fires the send button so the concierge flow starts
     with the product context already in the thread. */
  window.SolumChatSendAction = function (payload, label) {
    var root = document.querySelector("[data-solum-concierge]");
    if (!root || typeof root._scSendAction !== "function") return false;
    root._scSendAction(payload, label);
    return true;
  };
  window.SolumChatPrefill = function (text, autosend) {
    var root = document.querySelector("[data-solum-concierge]");
    if (!root) return false;
    var input = root.querySelector(".sc-input");
    var send = root.querySelector(".sc-send");
    if (!input) return false;
    input.value = String(text || "");
    try { input.dispatchEvent(new Event("input", { bubbles: true })); } catch (_) {}
    input.focus();
    if (autosend && send) send.click();
    return true;
  };
  window.SolumStartPro = function (tier, market) {
    var root = document.querySelector("[data-solum-concierge]");
    if (!root) return false;
    root.setAttribute("data-mode", tier || "pro-signup");
    root.setAttribute("data-market", market || "kr");
    /* Cancel any pending greeting timer */
    if (root._greetingTimer) { clearTimeout(root._greetingTimer); root._greetingTimer = null; }
    /* Clear any existing messages before reset */
    var list = root.querySelector(".sc-list");
    var actions = root.querySelector(".sc-actions");
    if (list) list.innerHTML = "";
    if (actions) { actions.innerHTML = ""; actions.style.display = "none"; }
    var resetBtn = root.querySelector(".sc-reset");
    if (resetBtn) resetBtn.click();
    return true;
  };

  function initAll() {
    document.querySelectorAll("[data-solum-concierge]").forEach(initOne);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }
})();